import {ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {NgStyle} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {filter, Subject, take, takeUntil} from 'rxjs';
import {Container, Image, ImageBox, ImageBoxToggle, ImageGraph, ImageLabel, ImageSection} from "../../../../model/layout.model";
import {MeteoMeasurementsService} from "../../../../service/http/meteo-measurements.service";
import {MeteoLineGraphComponent} from "../meteo-line-graph/meteo-line-graph.component";
import {environment} from "../../../../../environments/environment";
import {TerminalGraphicService} from "../../../../service/http/terminal-graphic.service";
import {BoxOrt, StationForecastService, WindValues} from "../../../../service/http/station-forecast.service";
import {ForecastSnapshotService} from "../../../../service/http/forecast-snapshot.service";
import {LadeQuellenService} from '../../../../service/lade-quellen.service';
import {EventBusService} from "../../../../service/event-bus.service";
import {EventBusTypes} from "../../../../model/constants";
import {GrafikUmfang, MosOhneWerte, SyncPartner, WarnschildSpiegel} from "../../../../model/event-bus.model";
import {WarnschildSpiegelService} from "../../../../service/warnschild-spiegel.service";
import {KiReihenfolgeService} from "../../../../service/ki-reihenfolge.service";
import {RfaStore} from "../../../../store/rfa/rfa.store";
import {GraphicSource, TerminalGraphicDocumentDto} from "../../../../model/terminal-graphic.model";
import {KiGrafikService} from "../../../../service/ki-grafik.service";

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.scss'],
    // OnPush statt Eager – bewusst abweichend von den übrigen Komponenten.
    //
    // Eine Grafik-Seite trägt über 1700 Boxen in 208 Instanzen dieser Komponente.
    // Mit Eager prüft Angular bei JEDEM Ereignis den ganzen Baum: ein Tastendruck in
    // einer Box wertet die Bindungen aller anderen mit aus (Stil-Objekte, Icon-Pfade,
    // Farben). Beim ersten Öffnen kommt das je eintreffender Antwort erneut – dann
    // reagiert die Oberfläche kaum noch.
    //
    // Mit OnPush prüft Angular nur, was als geändert markiert ist. Ereignis-Handler
    // markieren ihre Ansicht selbst; für alles, was asynchron schreibt, tut es
    // markiereGeaendert(). Wer hier einen asynchronen Schreibweg ergänzt, MUSS das
    // mitziehen, sonst erscheint der Wert nicht.
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgStyle, MatIconModule, MeteoLineGraphComponent]
})
export class ImageComponent implements OnInit, OnDestroy {

  // Referenzraum der Koordinaten (AE-Komposition, wie in meteoeffects)
  readonly REF_WIDTH = 1920;
  readonly REF_HEIGHT = 1080;

  // Einheit, die (nur für Temperatur-Boxen) fest angezeigt wird und nicht editierbar ist
  readonly TEMPERATURE_UNIT = '°';

  // Kombi-Typ: links editierbare Temperatur, rechts editierbares Wetter-Symbol.
  static readonly TYPE_TEMP_ICON = 'tempicon';
  // Kombi-Typ (04.11.2026, "Lokalzeit am Samstag", Zeile AUSSICHTEN): links zwei Temperaturen
  // übereinander – oben der Höchstwert (Hauptwert, wie die Temperatur der tempicon-Box), darunter
  // der Tiefstwert unter '<boxId>.min' –, rechts das Wetter-Symbol wie bei der tempicon-Box.
  static readonly TYPE_MAX_MIN_ICON = 'maxminicon';
  private static readonly MIN_KEY_SUFFIX = '.min';
  // Wind-Typ: oben Mittelwind, unten Böen, rechts ein drehbarer Windpfeil.
  static readonly TYPE_WIND = 'wind';
  // Böen-Typ: eine einzelne Zahl (Böen in Bft) aus denselben Stations-Windwerten wie die
  // Wind-Box – Lokalzeit Aachen, Aussichtentage ("Böen (Bft.)"). Sonst eine gewöhnliche Wert-Box.
  static readonly TYPE_BOEEN = 'boeen';
  private static readonly GUST_KEY_SUFFIX = '.boeen';
  private static readonly DIR_KEY_SUFFIX = '.dir';
  // Windrichtung 1..16 nach der Tabelle der Redaktion (Meteoeffects): 1 = Süd, dann je
  // 22,5 Grad im Uhrzeigersinn weiter (5 = West, 9 = Nord, 13 = Ost) – siehe WIND_RICHTUNGEN.
  private static readonly DIR_STEP_DEGREES = 22.5;
  private static readonly DIR_MAX = 16;
  // Sonderwerte 17 und 18: wechselnde / verschiedene / unterschiedliche Richtungen.
  // Statt des geraden Pfeils wird ein Kreispfeil gezeigt.
  private static readonly DIR_ROTATING = [17, 18];
  /**
   * Die Richtungstabelle der Redaktion: Zahl -> Richtung, aus der der Wind kommt.
   *
   * Spiegel von `WindDirectionMapping` im Rest-Service und von `wind_bedeutung` im
   * AI-Modul – wer hier etwas ändert, muss beide mitziehen.
   */
  private static readonly WIND_RICHTUNGEN: ReadonlyMap<number, string> = new Map([
    [1, 'Süd'],
    [2, 'Süd bis Südwest'],
    [3, 'Südwest'],
    [4, 'Südwest bis West'],
    [5, 'West'],
    [6, 'West bis Nordwest'],
    [7, 'Nordwest'],
    [8, 'Nordwest bis Nord'],
    [9, 'Nord'],
    [10, 'Nord bis Nordost'],
    [11, 'Nordost'],
    [12, 'Nordost bis Ost'],
    [13, 'Ost'],
    [14, 'Ost bis Südost'],
    [15, 'Südost'],
    [16, 'Südost bis Süd']
  ]);
  private static readonly WIND_WECHSELND = 'wechselnde / verschiedene / unterschiedliche Richtungen';
  // Schlüssel-Suffix, unter dem der Icon-Anteil in der values-Map steht
  private static readonly ICON_KEY_SUFFIX = '.icon';

  @Input() image?: Image;

  // Id der Box, die aktuell bearbeitet wird (null = keine)
  editingId: string | null = null;
  hoveredId: string | null = null;

  /**
   * Verbindungs-Pfeile dieser Karte (Oktober 2026): Quell-Box-Id → Ziel-Box-Id. Gezogen vom
   * Halbkreis-Griff unter einer angehakten Box auf eine andere angehakte Box derselben Karte;
   * im Dokument als `ki:pfeile` ({@link KiGrafikService.PFEILE_SCHLUESSEL}), in der KI-Anfrage
   * als `pfeil_icon`/`pfeil_wind`. Eine Box hat höchstens einen ausgehenden Pfeil.
   */
  private pfeile = new Map<string, string>();
  /** Ein Pfeil wird gerade gezogen: von welcher Box, und wo der Zeiger steht (Referenzraum). */
  pfeilZiehen: {von: ImageBox; x: number; y: number; bewegt: boolean} | null = null;
  /** Abmelder der Dokument-Listener, solange ein Pfeil gezogen wird. */
  private pfeilListener: (() => void)[] = [];
  /** Der per Klick ausgewählte Pfeil (Quell-Box-Id): er zeigt sein X zum Entfernen. */
  pfeilAusgewaehlt: string | null = null;
  /** Abmelder des Dokument-Listeners, der die Auswahl bei einem Klick anderswo aufhebt. */
  private pfeilAuswahlListener: (() => void) | null = null;

  // Box, deren Regions-Hinweis gerade sichtbar ist, sowie der Timer bis zum Einblenden.

  // Aktivierte Umschalter, Schlüssel "boxId::toggleId". Reine Anzeige, wird nicht gespeichert.
  private readonly activeToggles = new Set<string>();

  // Aktuell in der Sidebar ausgewählter Abschnitt
  // Karten-eigene Bedienelemente (Sync-Schalter, Laden, Leeren)
  syncAktiv = false;
  /** Wert des zuletzt bearbeiteten Wetter-Symbols dieser Karte – Quelle des Sync. */
  private letzterIconWert: string | null = null;
  /** Stand der Symbol-Werte beim Einschalten des Sync, für die Wiederherstellung. */
  private readonly syncVorher = new Map<string, string>();

  activeSectionId: string | null = null;
  activeSection?: ImageSection;

  // Alle Messwerte der geladenen Datei (Schlüssel -> Wert) + Cache je Abschnitt (Abschnitt -> BoxId -> Wert)
  private measurements = new Map<string, string>();
  private readonly sectionValues = new Map<string, Map<string, string>>();
  private static readonly DEFAULT_BUCKET = '__default__';
  /** Trennt Box- und Umschalter-Id im Wert-Schlüssel eines gesetzten Umschalters. */
  private static readonly TOGGLE_SEPARATOR = '::';

  /**
   * Präfix der Schlüssel in `image.values`, die **nicht** dieser Komponente gehören.
   *
   * Darunter legt der {@link KiGrafikService} die erzeugten KI-Texte ab
   * (`KiGrafikService.ABLAGE_PRAEFIX`) – wer eines von beiden ändert, muss das andere
   * mitändern. Ein **einzelner** Doppelpunkt kommt in keinem Box-Schlüssel vor, die
   * Werte können sich also nicht in die Wege kommen.
   */
  private static readonly WERT_PRAEFIX_FREMD = 'ki:';

  // Quellen-Sidebar (FILE/DOC): immer sichtbar; Beschriftung wird um den Zeitstempel ergänzt,
  // sobald die jeweilige Quelle geladen ist.
  sourceButtons: { source: GraphicSource; label: string }[] = [
    {source: 'TXT', label: 'FILE'},
    {source: 'DOC', label: 'DOC'}
  ];
  activeSource: GraphicSource | null = 'TXT';
  private readonly sourceImages = new Map<GraphicSource, Image>();
  private userSelectedSource = false;

  private readonly destroyer$ = new Subject<boolean>();

  constructor(private measurementsService: MeteoMeasurementsService,
              private graphicService: TerminalGraphicService,
              private stationForecastService: StationForecastService,
              private forecastSnapshotService: ForecastSnapshotService,
              private eventBusService: EventBusService,
              private ladeQuellenService: LadeQuellenService,
              private rfaStore: RfaStore,
              private changeDetector: ChangeDetectorRef,
              private reihenfolge: KiReihenfolgeService,
              private host: ElementRef<HTMLElement>) {
  }

  ngOnInit(): void {
    const sections = this.image?.sections;
    if (sections && sections.length > 0) {
      this.activeSection = sections[0];
      this.activeSectionId = this.sectionKey(sections[0]);
    }
    // Sind die Werte bereits server-seitig aufgelöst (Redis-Cache), diese direkt übernehmen
    // und KEINEN eigenen Fetch machen. Andernfalls – nur wenn erlaubt – wie bisher aus der
    // MOS-Datei laden. In der Cloud ist der Direkt-Fetch deaktiviert (Local Network Access),
    // dann werden lediglich die Startwerte (z.B. Icon-Index) angezeigt.
    if (this.hasEmbeddedValues()) {
      this.loadEmbeddedValues();
    } else {
      // Kein Abruf beim Seitenaufbau – weder Stationen noch MOS-Datei. Die Boxen starten
      // leer und werden erst auf "MSWR-MOS laden" gefüllt. Was hier passiert, ist nur das
      // Setzen der Startwerte aus dem Template (z.B. Mondphase).
      this.buildSectionValues();
      this.applyActiveSection();
    }
    this.ladePfeile();
    // Die MSWR-MOS-Werte werden beim Seitenaufbau BEWUSST NICHT geholt – die Boxen
    // starten leer. Erst ein Klick auf "MSWR-MOS laden" trägt sie ein, entweder über
    // den Button oben auf der Seite (Event) oder den kleinen Button in der Karte
    // (onKarteLaden). Damit steht der Redakteur nicht vor Werten, die er nicht
    // angefordert hat, und der Seitenaufbau löst keinen Snapshot-Abruf aus.
    // "MSWR-MOS laden": nur wenn dieses Bild zum Umfang des Knopfes gehört. Der Knopf
    // der Kopfleiste gilt für alles, der an einer Zeilenüberschrift nur für ihre Karten.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.MSWR_LOAD_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$))
      .subscribe(umfang => {
        if (this.istGemeint(umfang?.kartenIds)) {
          this.ladeWerte(umfang);
        }
      });
    // "leeren": nur die Boxen dieses Bildes, und nur wenn es zur gemeinten Zeile gehört.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.CLEAR_BOXES_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$))
      .subscribe(umfang => {
        if (this.istGemeint(umfang?.kartenIds)) {
          this.clearAllBoxes();
        }
      });
    // "KI-Auswahl aufheben": alle gelben Kästchen dieser Karte abhaken, wenn sie zur
    // gemeinten Zeile gehört – die Werte bleiben stehen.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.KI_AUSWAHL_AUFHEBEN_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$))
      .subscribe(umfang => {
        if (this.istGemeint(umfang?.kartenIds)) {
          this.kiAuswahlAufheben();
        }
      });
    // Werte von außen in den Dokumentbaum geschrieben ("für COSMO übernehmen"): die Anzeige
    // aus image.values neu aufbauen – sonst zeigte eine schon gerenderte Karte die alten.
    this.eventBusService.on$<GrafikUmfang>(EventBusTypes.GRAFIK_WERTE_GEAENDERT_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$))
      .subscribe(umfang => {
        if (umfang?.kartenIds?.length && this.istGemeint(umfang.kartenIds)) {
          this.ladeAusDokument();
        }
      });
    // Sync der Partnerkarte (feine ↔ sehr feine Unterteilung derselben Zeile) umgelegt:
    // hier mitziehen, als wäre der eigene Schalter gedrückt worden – ohne das Event
    // zurückzuschicken, sonst pingten sich die beiden Karten endlos an.
    this.eventBusService.on$<SyncPartner>(EventBusTypes.SYNC_PARTNER_EVENT.valueOf())
      .pipe(takeUntil(this.destroyer$))
      .subscribe(partner => {
        if (partner?.partnerId && partner.partnerId === this.image?.containerId) {
          this.folgeSyncDesPartners(partner);
        }
      });
    // Zusätzlich die Quellen (FILE/DOC) laden und die zweite Sidebar aufbauen.
    this.loadSources();
  }

  // Warnung nur einmal ausgeben (nicht pro Bild), wenn kein Fetch erfolgt.
  private static fallbackWarned = false;

  /**
   * Lädt für die aktuelle RFA beide Quellen (TXT/DOC) und baut die Quellen-Sidebar auf.
   * Nur relevant für Grafik-Bilder, die in den gecachten Quell-Dokumenten enthalten sind.
   */
  /**
   * Holt je Box mit Region und Reihe das Wetter-Symbol vom Terminal-Rest-Service und
   * schreibt es in die Box. Region und Reihe gehen als Parameter mit, das Backend löst
   * daraus Station, Sammlung, Tag und Zeitspanne auf.
   *
   * Läuft einmal beim Seitenaufbau und danach bei jedem Klick auf "MSWR-MOS laden".
   * Die Karten bleiben dabei stehen; jede Antwort trägt sich einzeln in ihre Box ein,
   * die Werte erscheinen also nach und nach.
   *
   * Boxen ohne Station bleiben unberührt und lösen keinen Aufruf aus. Liefert der
   * Server 204 (Reihe noch nicht gemappt), bleibt die Box unverändert.
   */
  /**
   * "MSWR-MOS laden": holt die Werte dieses Bildes – über den Weg, den es kennt.
   *
   * Es gibt zwei Datenwege, und ein Bild gehört immer genau zu einem: die Lokalzeit-Karten
   * hängen an einer MOS-Vorhersagedatei der Wetterdaten-API (`measurementsFile` + `dataKey`
   * je Box), alle übrigen an den MSWR-Stationen (`station`/`region`/`row`). Der Knopf
   * wählt danach aus. Ausnahme in der Lokalzeit: die Heute- und Aussichtentag-Karten tragen
   * keine Datei (die MOS-Dateien haben dafür keine Orts-Werte), sondern `station` + `row` und
   * gehen deshalb den Stationsweg – über die Station statt über eine Region, siehe
   * `stationsOrt()`. Und eine MOS-Karte kann einzelne Stations-Boxen tragen: die Wind-Box
   * der Nacht- und Morgen-Karten hat `station` + `row`, weil die MOS-Datei weder Bft noch
   * Böen noch Nachtwind kennt. Für sie läuft der Stationsweg **zusätzlich** – er fasst nur
   * Boxen mit `row` an, die MOS-Boxen bleiben ihm fremd.
   */
  private ladeWerte(umfang?: GrafikUmfang): void {
    // `umfang` ist der Knopf, der geladen hat (Reiter oder Zeile): die Karte meldet ihm, aus
    // welchen Quellen sie geladen hat (LadeQuellenService); der kleine Knopf an der Karte
    // bringt keinen und meldet nichts.
    const karte = this.image?.containerId ?? this.image?.caption ?? '(ohne Kennung)';
    if (this.image?.graph) {
      // Tendenz-Karte ohne Abschnitt: der Graph hängt am Bild und bekommt seine Punkte hier –
      // die Diagramm-Komponente selbst lädt bewusst nichts (nicht beim Seitenaufbau, in der
      // Cloud gar nicht). Eine Tendenz-Karte hat keine Boxen, der Box-Weg darunter läuft leer.
      this.ladeTendenz(karte, umfang);
    }
    if (this.image?.measurementsFile) {
      console.debug(`MSWR-MOS laden: ${karte} -> MOS-Datei ${this.image.measurementsFile}`);
      this.loadMeasurements(umfang);
      if (!this.hatStationsBoxen()) {
        return;
      }
      console.debug(`MSWR-MOS laden: ${karte} -> zusätzlich Stations-Vorhersage für die Boxen mit Reihe`);
    } else {
      console.debug(`MSWR-MOS laden: ${karte} -> Stations-Vorhersage`);
    }
    this.loadStationForecasts(umfang);
  }

  /** Trägt diese Karte Boxen, die über Station und Reihe nachschlagen (Lokalzeit-Wind auf Nacht/Morgen)? */
  private hatStationsBoxen(): boolean {
    return this.allBoxes().some(box => !!box.row && this.stationsOrt(box) !== null);
  }

  private loadStationForecasts(umfang?: GrafikUmfang): void {
    if (!environment.useForecastSnapshot) {
      this.ladeBoxweise(this.allBoxes(), umfang);
      return;
    }
    // Ein Aufruf für die ganze Seite; über 200 Bild-Komponenten teilen sich ihn.
    this.forecastSnapshotService.load().pipe(take(1)).subscribe(snapshot => {
      if (snapshot === null) {
        // Kein Snapshot auf dem Server (noch kein Refresh gelaufen) oder Aufruf
        // fehlgeschlagen -> auf den alten Weg zurückfallen, statt leer zu bleiben.
        this.ladeBoxweise(this.allBoxes(), umfang);
        return;
      }
      this.uebernehmeSnapshot(umfang);
    });
  }

  /**
   * Trägt die Werte dieses Bildes aus dem geladenen Snapshot ein – ohne weiteren
   * HTTP-Aufruf, die Werte liegen bereits im Client.
   *
   * Ausnahme: Boxen, die über ihre **Station** nachschlagen (Lokalzeit Heute/Aussichtentage) und im
   * Snapshot nichts finden, holen ihre Werte einzeln nach. Das passiert, wenn der Snapshot
   * in Redis noch vom Stand vor den Lokalzeit-Stationen stammt (kein
   * `refreshTerminalForecasts` seit dem Update) oder eine Station beim Refresh ausfiel –
   * ohne Rückfall blieben die Boxen dann stumm leer. Der Endpunkt `?station=` fragt in dem
   * Fall direkt die Wetterdaten-API. NRW-Boxen (Region) behalten das bisherige Verhalten.
   */
  private uebernehmeSnapshot(umfang?: GrafikUmfang): void {
    const nachladen: ImageBox[] = [];
    for (const box of this.allBoxes()) {
      const ort = this.stationsOrt(box);
      if (!ort || !box.row) {
        continue;
      }
      // Für den Kasten "MSWR-MOS geladen": welche Reihe und welchen Ort diese Box benutzt hat –
      // der Rest-Service macht daraus Pakete und Stationen.
      this.ladeQuellenService.merkeAbfrage(umfang, {reihe: box.row, region: ort.region ?? null,
        station: ort.station ?? null, wind: this.isWind(box) || this.isBoeen(box),
        temperatur: this.hatTemperatur(box)});
      let gefunden = false;
      if (this.isWind(box) || this.isBoeen(box)) {
        const werte = this.forecastSnapshotService.wind(ort, box.row);
        if (werte && this.isBoeen(box)) {
          gefunden = this.applyBoeen(box, werte);
        } else if (werte) {
          this.applyWindValues(box, werte);
          gefunden = true;
        }
      } else {
        const symbol = this.forecastSnapshotService.symbol(ort, box.row);
        if (symbol !== null) {
          this.applyStationValue(box, String(symbol));
          gefunden = true;
        }
        // Kombi-Boxen tragen zwei Werte: das Symbol rechts, die Temperatur links.
        if (this.hatTemperatur(box)) {
          const temperatur = this.forecastSnapshotService.temperatur(ort, box.row);
          if (temperatur !== null) {
            this.applyTemperatureValue(box, String(temperatur));
            gefunden = true;
          }
        }
      }
      // Kennt der Snapshot den Ort dieser Box nicht, holt sie ihren Wert einzeln – für
      // Lokalzeit-Stationen wie für NRW-/COSMO-Regionen. Der Fall ist derselbe: der Snapshot
      // ist älter als die Stationsdatei (etwa eine neue Region wie "NRW ganz" auf der Wind-
      // Karte), und `forecast/symbol|wind` greift dann auf die Wetterdaten-API durch.
      if (!gefunden) {
        nachladen.push(box);
      }
    }
    this.markiereGeaendert();
    if (nachladen.length > 0) {
      ImageComponent.meldeSnapshotOhneStationen(this.forecastSnapshotService.erstellt());
      this.ladeBoxweise(nachladen, umfang);
    }
  }

  /**
   * Einmal je Seite: der Snapshot kennt Orte (Lokalzeit-Stationen oder Regionen) nicht.
   * Kein Fehler – die Boxen holen ihre Werte einzeln –, aber ein Hinweis, dass ein Refresh
   * fällig ist.
   */
  private static meldeSnapshotOhneStationen(stand: string | null): void {
    if (ImageComponent.snapshotOhneStationenGemeldet) {
      return;
    }
    ImageComponent.snapshotOhneStationenGemeldet = true;
    console.warn(`Forecast-Snapshot (Stand ${stand}) enthält Werte einzelner Boxen nicht `
      + '(Lokalzeit-Stationen oder Regionen, die erst nach dem Snapshot in die Stationsdatei kamen) – '
      + 'diese Boxen werden einzeln geladen. Abhilfe: POST refreshTemplateList und '
      + 'POST refreshTerminalForecasts am Rest-Service auslösen.');
  }

  private static snapshotOhneStationenGemeldet = false;

  /**
   * Woran eine Box ihren Stationswert festmacht – oder null, wenn sie keinen hat.
   *
   * NRW- und COSMO-Karten (`wdr.grafik.*`, `wdr.cosmo.*`): Region (das Backend kennt die
   * Station dazu). Lokalzeit-Karten (`wdr.lokalzeit.*`): die Station direkt an der Box – aus der
   * Stationsdatei `wdr-lokalzeiten-stations.json` des Backends, das sie in jedes Dokument
   * einträgt. Ihre `region` ist die Quickinfo der Karte ("Jülicher Börde", "Kirchheller Heide")
   * für Tooltip und KI-Anfrage, aber **kein** Schlüssel der NRW-Stationstabellen – über sie
   * nachzuschlagen fände nichts. ⚠️ Deshalb entscheidet die **Karte**, nicht die Box: früher
   * galt `city` (der MOS-Ort) als Kennzeichen einer Lokalzeit-Box, aber Boxen ohne MOS-Ort
   * ("Köln" in Bonn, "Kempener Land", "Kirchheller Heide", "Hochlagen", "Teutoburger Wald")
   * haben kein `city`, wohl aber Region und Station – sie liefen als NRW-Region und blieben
   * leer. Ohne `station` bleibt eine Box ganz außen vor (Richtungs-Karte, Warnschild).
   */
  private stationsOrt(box: ImageBox): BoxOrt | null {
    if (!box.station) {
      return null;
    }
    const lokalzeit = (this.image?.containerId ?? '').startsWith('wdr.lokalzeit.');
    return box.region && !box.city && !lokalzeit ? {region: box.region} : {station: box.station};
  }

  /**
   * Meldet einen fehlgeschlagenen Wert-Abruf – aber nur die ersten paar.
   *
   * Der boxweise Ladeweg fragt bis zu 1092 Werte einzeln ab. Ist der Service nicht
   * erreichbar, schlagen alle fehl; ohne Deckel wären das 1092 Zeilen in der Konsole,
   * die jede andere Meldung verdrängen. Der Zähler ist absichtlich statisch: die Flut
   * entsteht über alle Bild-Komponenten hinweg, nicht innerhalb einer.
   */
  private meldeLadefehler(text: string): void {
    ImageComponent.ladefehler++;
    if (ImageComponent.ladefehler <= ImageComponent.MAX_LADEFEHLER) {
      console.warn(text);
    } else if (ImageComponent.ladefehler === ImageComponent.MAX_LADEFEHLER + 1) {
      console.warn('Weitere Meldungen dieser Art werden nicht mehr einzeln ausgegeben. '
        + 'Die Ursache steht in den Zeilen darüber.');
    }
  }

  private static ladefehler = 0;
  private static readonly MAX_LADEFEHLER = 3;

  /**
   * Meldet Angular, dass diese Ansicht neu geprüft werden muss.
   *
   * Nötig, weil die Werte aus asynchronen Rückläufen kommen (HTTP) und dabei nur
   * Felder beschrieben werden. Die Strategie {@code Eager} prüft die Komponente zwar
   * immer – aber nur, wenn die Traversierung sie erreicht. Ohne diese Markierung kann
   * der Hauptdurchlauf sie überspringen; die Änderung fällt dann erst im
   * Verifikationsdurchlauf auf und endet in NG0100 statt in einer Anzeige. Genau daran
   * scheiterte zuvor der Regions-Hinweis beim Hovern.
   */
  private markiereGeaendert(): void {
    this.changeDetector.markForCheck();
  }

  /**
   * Der bisherige Ladeweg: ein Request je Box. Bleibt unverändert bestehen und ist
   * über {@code environment.useForecastSnapshot = false} wieder der einzige Weg –
   * sowie automatisch die Rückfallebene, wenn kein Snapshot vorliegt (oder ihm die
   * Werte einzelner Stations-Boxen fehlen, dann nur für diese).
   */
  private ladeBoxweise(boxen: ImageBox[] = this.allBoxes(), umfang?: GrafikUmfang): void {
    if (boxen.some(box => !!box.row && this.stationsOrt(box) !== null)) {
      // Kein Zeitstempel: die Wetterdaten-API antwortet ohne Stand, es zählt der Abrufzeitpunkt.
      this.ladeQuellenService.melde(umfang, {art: 'einzelabruf', name: 'Wetterdaten-API', stand: null});
    }
    for (const box of boxen) {
      const ort = this.stationsOrt(box);
      if (!ort || !box.row) {
        continue;
      }
      this.ladeQuellenService.merkeAbfrage(umfang, {reihe: box.row, region: ort.region ?? null,
        station: ort.station ?? null, wind: this.isWind(box) || this.isBoeen(box),
        temperatur: this.hatTemperatur(box)});
      const region = ort.region ?? ort.station;
      const reihe = box.row;
      // Wind-Boxen tragen drei Werte und hängen an einer eigenen Stations-Tabelle,
      // deshalb ein eigener Endpunkt.
      if (this.isWind(box) || this.isBoeen(box)) {
        this.stationForecastService.fetchWind(ort, reihe).pipe(take(1)).subscribe({
          next: werte => {
            if (werte === null) {
              return;   // keine Zuordnung – Template-Wert behalten
            }
            if (this.isBoeen(box)) {
              this.applyBoeen(box, werte);
            } else {
              this.applyWindValues(box, werte);
            }
            this.markiereGeaendert();
          },
          error: err => this.meldeLadefehler(
            `Wind-Werte für ${region} / ${reihe} nicht ladbar (Status ${err?.status})`)
        });
        continue;
      }
      // Kombi- und Temperatur-Boxen brauchen zusätzlich die Temperatur. Nur in diesem
      // Rückfall-Pfad ein zweiter Abruf – über den Snapshot kommt sie mit derselben
      // Antwort.
      if (this.hatTemperatur(box)) {
        this.stationForecastService.fetchTemperature(ort, reihe).pipe(take(1)).subscribe({
          next: temperatur => {
            if (temperatur === null) {
              return;
            }
            this.applyTemperatureValue(box, String(temperatur));
            this.markiereGeaendert();
          },
          error: err => this.meldeLadefehler(
            `Temperatur für ${region} / ${reihe} nicht ladbar (Status ${err?.status})`)
        });
      }
      this.stationForecastService.fetchSymbol(ort, reihe).pipe(take(1)).subscribe({
        next: symbol => {
          if (symbol === null) {
            return;   // keine Zuordnung – Template-Wert behalten
          }
          this.applyStationValue(box, String(symbol));
          this.markiereGeaendert();
        },
        error: err => this.meldeLadefehler(
          `Wetter-Symbol für ${region} / ${reihe} nicht ladbar (Status ${err?.status})`)
      });
    }
  }

  /**
   * Übernimmt einen vom Server gelieferten Wert in eine Box: in den Wert-Cache des
   * aktiven Abschnitts, damit er einen Abschnittswechsel übersteht, und direkt in die
   * Anzeige. Bei Kombi-Boxen betrifft das den Symbol-Anteil, sonst den Hauptwert.
   */
  private applyStationValue(box: ImageBox, wert: string): void {
    if (!this.darfSchreiben(box)) {
      return;
    }
    const schluessel = this.istKombi(box) ? box.id + ImageComponent.ICON_KEY_SUFFIX : box.id;
    this.rememberValue(schluessel, wert);
    if (this.istKombi(box)) {
      box.iconLabel = wert;
    } else {
      box.label = wert + this.unit(box);
    }
  }

  /**
   * Übernimmt eine Temperatur in eine Box: in den Hauptwert, also die linke Zelle der
   * Kombi-Boxen (bei der Höchst-/Tiefstwert-Box den oberen Kasten, den Höchstwert – der
   * Tiefstwert hat bislang keinen Stationsweg und bleibt Eingabe) bzw. die ganze Box bei
   * reinen Temperatur-Boxen.
   *
   * Der Schlüssel ist die Box-Id ohne Zusatz – genau der, den {@code applyActiveSection}
   * in {@code box.label} zurückschreibt. Die Einheit kommt aus {@code unit(box)}, und die
   * Farbe der Zelle ergibt sich automatisch aus dem Wert (Temperatur-Palette).
   */
  private applyTemperatureValue(box: ImageBox, wert: string): void {
    if (!this.darfSchreiben(box)) {
      return;
    }
    this.rememberValue(box.id, wert);
    box.label = wert + this.unit(box);
  }

  /** Böen-Box: aus den Wind-Werten der Station nur die Böen (Bft) als Hauptwert. */
  private applyBoeen(box: ImageBox, werte: WindValues): boolean {
    if (werte.boeen === null || werte.boeen === undefined) {
      return false;
    }
    this.applyStationValue(box, String(werte.boeen));
    return true;
  }

  /**
   * Übernimmt die drei Wind-Werte einer Box: Mittelwind in den oberen Kasten
   * (Hauptwert), Böen in den unteren und die Richtungszahl in das Feld im Pfeil.
   * Fehlende Einzelwerte lassen ihren Kasten unangetastet.
   *
   * Wie bei den Symbol-Boxen laufen die Werte zusätzlich in den Wert-Cache des
   * aktiven Abschnitts, damit sie einen Abschnittswechsel überstehen.
   */
  private applyWindValues(box: ImageBox, werte: WindValues): void {
    if (!this.darfSchreiben(box)) {
      return;
    }
    if (werte.mittel !== null && werte.mittel !== undefined) {
      this.rememberValue(box.id, String(werte.mittel));
      box.label = String(werte.mittel) + this.unit(box);
    }
    if (werte.boeen !== null && werte.boeen !== undefined) {
      this.rememberValue(box.id + ImageComponent.GUST_KEY_SUFFIX, String(werte.boeen));
      box.gustLabel = String(werte.boeen);
    }
    if (werte.richtung !== null && werte.richtung !== undefined) {
      this.rememberValue(box.id + ImageComponent.DIR_KEY_SUFFIX, String(werte.richtung));
      box.dirLabel = String(werte.richtung);
    }
  }

  /**
   * Darf in diese Box geschrieben werden?
   *
   * Nein, solange der Redakteur sie bearbeitet. Die Werte treffen asynchron ein –
   * beim ersten Öffnen strömen sie für über 1700 Boxen herein. Ohne diese Sperre
   * würde ein eintreffender Wert dem Redakteur mitten im Tippen den Text ersetzen;
   * genau das ließ die Boxen "nicht editierbar" wirken.
   *
   * Übersprungen wird beides, Anzeige UND Wert-Cache: was der Redakteur eintippt,
   * hat Vorrang und darf nicht nachträglich vom Server überschrieben werden – auch
   * nicht beim nächsten Abschnittswechsel.
   */
  private darfSchreiben(box: ImageBox): boolean {
    return this.editingId !== box.id;
  }

  /**
   * Legt einen Wert im Wert-Cache des aktiven Abschnitts ab und spiegelt ihn in den
   * Dokumentbaum, damit der Speichern-Knopf ihn mitschickt.
   *
   * Ein leerer Wert wird nicht als Leerstring abgelegt, sondern der Schlüssel entfernt:
   * eine bewusst leer gelassene Box darf im gespeicherten Datensatz nicht auftauchen.
   */
  private rememberValue(schluessel: string, wert: string): void {
    let bucket = this.sectionValues.get(this.activeKey);
    if (!bucket) {
      bucket = new Map<string, string>();
      this.sectionValues.set(this.activeKey, bucket);
    }
    if (wert === '') {
      bucket.delete(schluessel);
    } else {
      bucket.set(schluessel, wert);
    }
    this.spiegleInsDokument();
    this.meldeWarnschildSpiegel(schluessel, wert);
  }

  /**
   * Gespiegelte Warnschilder (Prognose-Zeile "Tag" ↔ Warnschild der Aussichtentag-Karte 3, s.
   * {@link WarnschildSpiegelService}): hat die Box einen Partner, erfährt der von der Änderung –
   * über das Dokument, denn die Partner-Karte liegt in einem anderen Reiter. Nur Eingabe und
   * "leeren" laufen hier durch; das Neuladen aus dem Dokument (Partner-Seite) nicht, deshalb
   * kein Hin und Her.
   */
  private meldeWarnschildSpiegel(boxId: string, wert: string): void {
    const karte = this.image?.containerId;
    if (karte && WarnschildSpiegelService.partnerVon(karte, boxId)) {
      this.eventBusService.emit(EventBusTypes.WARNSCHILD_SPIEGEL_EVENT.valueOf(), new WarnschildSpiegel(karte, boxId, wert));
    }
  }

  /**
   * Überträgt den Wert-Cache in `image.values` bzw. `section.values`.
   *
   * Das ist der Grund, warum der Speichern-Knopf ohne Umweg funktioniert: die
   * Element-Komponenten arbeiten direkt auf den `Container`-Objekten des Dokuments,
   * also trägt das POST-Dokument die Werte bereits, ohne dass jemand die 208
   * Bild-Komponenten einsammeln müsste. Leere Abschnitte bleiben `undefined`, damit
   * kein leerer Datensatz entsteht.
   */
  private spiegleInsDokument(): void {
    if (!this.image) {
      return;
    }
    this.image.values = ImageComponent.mitFremdenWerten(this.image.values,
      this.bucketAlsObjekt(ImageComponent.DEFAULT_BUCKET));
    for (const section of this.image.sections ?? []) {
      section.values = this.bucketAlsObjekt(this.sectionKey(section));
    }
  }

  /**
   * Rettet die fremden Werte über das Spiegeln hinweg.
   *
   * In `image.values` liegen nicht nur Box-Werte: unter dem Präfix `ki:` legt der
   * {@link KiGrafikService} die erzeugten KI-Texte ab, damit sie mit dem Datensatz
   * gespeichert werden – dieselbe Karte ist der einzige Ort, der überhaupt persistiert
   * wird. Diese Komponente überschreibt das Feld bei **jedem** Wert-Wechsel mit ihrem
   * eigenen Cache; ohne das Zurückschreiben hier wäre eine gespeicherte Antwort beim
   * ersten Tastendruck in einer Box verloren.
   */
  private static mitFremdenWerten(alt: {[boxId: string]: string} | undefined,
                                  neu: {[boxId: string]: string} | undefined):
                                  {[boxId: string]: string} | undefined {
    const fremde = Object.entries(alt ?? {}).filter(([key]) => ImageComponent.istFremd(key));
    if (fremde.length === 0) {
      return neu;
    }
    return {...(neu ?? {}), ...Object.fromEntries(fremde)};
  }

  /** Ist das ein fremder Schlüssel, den diese Komponente nur durchreicht? */
  private static istFremd(schluessel: string): boolean {
    return schluessel.startsWith(ImageComponent.WERT_PRAEFIX_FREMD);
  }

  /** Ein Wert-Bucket als einfaches Objekt, oder undefined wenn er nichts enthält. */
  private bucketAlsObjekt(schluessel: string): { [boxId: string]: string } | undefined {
    const bucket = this.sectionValues.get(schluessel);
    if (!bucket || bucket.size === 0) {
      return undefined;
    }
    const objekt: { [boxId: string]: string } = {};
    bucket.forEach((wert, key) => {
      if (ImageComponent.istWert(wert)) {
        objekt[key] = wert;
      }
    });
    return Object.keys(objekt).length > 0 ? objekt : undefined;
  }

  /**
   * Gehört dieses Bild zum Umfang einer Zeilen-Aktion ("leeren", "MSWR-MOS laden")?
   *
   * Die Zeile nennt die `containerId` ihrer Karten – eindeutig, anders als die
   * Zeilenüberschrift, die sich zwischen den Ober-Reitern wiederholt. Eine leere Liste
   * oder gar keine Angabe meint **alle** Bilder; so wirken die Knöpfe der Kopfleiste
   * weiter seitenweit.
   */
  private istGemeint(kartenIds: string[] | null | undefined): boolean {
    if (!kartenIds || kartenIds.length === 0) {
      return true;
    }
    return !!this.image?.containerId && kartenIds.includes(this.image.containerId);
  }

  /**
   * Leert sämtliche Boxen dieses Bildes – über alle Abschnitte hinweg und für jeden
   * Box-Typ: Symbol-Boxen, den Temperatur- und den Symbol-Anteil der Kombi-Boxen,
   * Mittelwind, Böen und Windrichtung sowie die große Wert-Box.
   *
   * Auch der Wert-Cache wird geleert, damit ein Abschnittswechsel die Werte nicht
   * wieder zurückholt. Die Karten und ihr Aufbau bleiben unangetastet.
   */
  private clearAllBoxes(): void {
    this.sectionValues.clear();
    for (const box of this.allBoxes()) {
      box.label = '';
      box.iconLabel = '';
      box.minLabel = '';
      box.gustLabel = '';
      box.dirLabel = '';
    }
    this.activeToggles.clear();
    if (this.image) {
      this.reihenfolge.entferneKarte(this.image);   // abgewählte Kästchen haben keine Nummer mehr
    }
    if (this.image?.graph?.points?.length) {
      // Tendenz-Karte: auch das Diagramm leeren – neue Referenz, s. ladeTendenz().
      this.image.graph = {...this.image.graph, points: []};
    }
    this.spiegleInsDokument();
    for (const box of this.allBoxes()) {
      this.meldeWarnschildSpiegel(box.id, '');   // ein gespiegeltes Warnschild wird mit geleert
    }
    this.pfeile.clear();              // ohne angehakte Kästchen gibt es nichts zu verbinden
    this.schreibePfeile();
    this.markiereGeaendert();
  }

  /**
   * "KI-Auswahl aufheben" (Knopf an der Zeilenüberschrift): hakt **alle gelben Kästchen**
   * (`ai`) dieser Karte ab und nimmt ihnen ihre Reihenfolge-Nummer. Alles andere bleibt:
   * Box-Werte, rote Kästchen (`warn`), der Schalter "Temp" (`ki:temp`) – es ist kein
   * "leeren", sondern nur die Rücknahme der KI-Auswahl. Läuft aus einem Bus-Callback,
   * deshalb am Ende {@link markiereGeaendert}.
   */
  private kiAuswahlAufheben(): void {
    const suffix = ImageComponent.TOGGLE_SEPARATOR + KiGrafikService.KI_UMSCHALTER;
    const gelbe = [...this.activeToggles].filter(key => key.endsWith(suffix));
    if (gelbe.length === 0) {
      return;
    }
    gelbe.forEach(key => this.activeToggles.delete(key));
    this.spiegleUmschalter();
    if (this.image) {
      // Alle gelben Kästchen sind ab, also alle Nummern dieser Karte weg – die größeren
      // der anderen Karten rücken nach.
      this.reihenfolge.entferneKarte(this.image);
    }
    this.pfeile.clear();
    this.schreibePfeile();
    this.markiereGeaendert();
  }

  /** Alle Boxen des Bildes, unabhängig vom aktiven Abschnitt. */
  private allBoxes(): ImageBox[] {
    const boxes: ImageBox[] = [...(this.image?.boxes ?? [])];
    for (const section of this.image?.sections ?? []) {
      boxes.push(...(section.boxes ?? []));
    }
    return boxes;
  }

  private loadSources(): void {
    // Ohne Quellenwahl gibt es nichts umzuschalten. Ohne diese Bedingung würden sich alle
    // 208 Bilder in den Quell-Dokumenten selbst suchen und ihr Bild gegen die Quellfassung
    // tauschen – die 196 Grafik-Karten sollen bei ihrer Vorlage bleiben.
    if (!this.image?.containerId || !this.hatQuellenwahl()) {
      return;
    }
    // Auf eine gesetzte RFA warten (BehaviorSubject startet mit undefined, wird erst nach dem
    // RFA-Load befüllt – sonst würde take(1) bei Seiten-Reload undefined greifen und nie laden).
    this.rfaStore.getSelectedRfa().pipe(filter(rfa => !!rfa?.kuerzel), take(1)).subscribe(rfa => {
      const kuerzel = rfa?.kuerzel;
      if (!kuerzel) {
        return;
      }
      (['TXT', 'DOC'] as GraphicSource[]).forEach(source => {
        this.graphicService.fetch(kuerzel, source).pipe(take(1)).subscribe({
          next: dto => this.registerSource(source, dto),
          error: err => console.warn(`Grafik-Quelle ${source} für ${kuerzel} nicht ladbar (Status ${err?.status}); Button wird nicht angezeigt.`)
        });
      });
    });
  }

  /** Registriert eine geladene Quelle: passendes Bild suchen, Button anlegen, ggf. anwenden. */
  private registerSource(source: GraphicSource, dto: TerminalGraphicDocumentDto): void {
    const containerId = this.image?.containerId;
    if (!dto?.document || !containerId) {
      return;
    }
    const sourceImage = this.findImageById(dto.document.tabs, containerId);
    if (!sourceImage) {
      return; // dieses Bild ist in dieser Quelle nicht enthalten
    }
    this.sourceImages.set(source, sourceImage);
    this.markiereGeaendert();
    // Beschriftung des (bereits sichtbaren) Buttons um den Zeitstempel ergänzen.
    const button = this.sourceButtons.find(b => b.source === source);
    if (button) {
      button.label = (source === 'TXT' ? 'FILE' : 'DOC') + ' - ' + this.formatTimestamp(dto.timestamp);
    }
    // Standard: FILE bevorzugen; anwenden, solange der Nutzer noch nicht selbst gewählt hat.
    if (!this.userSelectedSource && (source === 'TXT' || !this.sourceImages.has('TXT'))) {
      this.applySource(source);
    }
  }

  /** Klick auf einen Quellen-Button: diese Quelle anzeigen (falls geladen), sonst nur markieren. */
  onSourceClick(source: GraphicSource): void {
    this.userSelectedSource = true;
    if (this.sourceImages.has(source)) {
      this.applySource(source);
    } else {
      this.activeSource = source; // Button aktiv markieren, auch wenn (noch) keine Daten vorliegen
    }
  }

  /** Wendet das Bild der gewählten Quelle an (Bild tauschen + Abschnitte/Werte neu aufbauen). */
  private applySource(source: GraphicSource): void {
    const sourceImage = this.sourceImages.get(source);
    if (!sourceImage) {
      return;
    }
    this.activeSource = source;
    this.image = sourceImage;
    this.reinitImage();
  }

  /**
   * Liest die Werte und Umschalter neu aus `image.values` / `section.values`, ohne den
   * aktiven Abschnitt zu wechseln – für Werte, die jemand anderes in den Dokumentbaum
   * geschrieben hat. Eine Box, die gerade bearbeitet wird, behält ihre Eingabe
   * (`applyActiveSection` prüft `darfSchreiben`).
   */
  private ladeAusDokument(): void {
    this.sectionValues.clear();
    this.activeToggles.clear();
    if (this.hasEmbeddedValues()) {
      this.loadEmbeddedValues();
    } else {
      this.buildSectionValues();
      this.applyActiveSection();
    }
    this.ladePfeile();
    this.markiereGeaendert();
  }

  /** Baut Abschnitte + Werte nach einem Bildwechsel (Quellenwechsel) neu auf. */
  private reinitImage(): void {
    this.sectionValues.clear();
    this.activeToggles.clear();
    const sections = this.image?.sections;
    this.activeSection = sections && sections.length > 0 ? sections[0] : undefined;
    this.activeSectionId = this.activeSection ? this.sectionKey(this.activeSection) : null;
    if (this.hasEmbeddedValues()) {
      this.loadEmbeddedValues();
    } else {
      this.buildSectionValues();
      this.applyActiveSection();
    }
    this.ladePfeile();
  }

  /** Sucht rekursiv das Image mit der gegebenen containerId im Container-Baum. */
  private findImageById(containers: Container[] | undefined, id: string): Image | undefined {
    for (const container of containers ?? []) {
      if (container.containerType === 'Image' && container.containerId === id) {
        return container as unknown as Image;
      }
      const found = this.findImageById(container.containerList, id);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  /** Kurzformat des Zeitstempels für die Button-Beschriftung. */
  private formatTimestamp(timestamp?: string): string {
    if (!timestamp) {
      return '';
    }
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    return date.toLocaleString('de-DE', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'});
  }

  /** Prüft, ob der Server bereits aufgelöste Box-Werte mitgeliefert hat. */
  private hasEmbeddedValues(): boolean {
    return ImageComponent.hatEigeneWerte(this.image?.values)
      || !!this.image?.sections?.some(section => !!section.values);
  }

  /**
   * Zählt dieses `values`-Feld als "vom Server geliefert"?
   *
   * Ein vorhandenes, aber leeres Feld zählt – wie bisher: es unterscheidet ein
   * gespeichertes Bild ohne Werte von einem, für das der Server gar nichts geschickt hat.
   * Ein Feld, das **ausschließlich fremde** Schlüssel trägt (die abgelegten KI-Antworten),
   * zählt dagegen wie ein fehlendes: sonst verlöre eine Karte, an der eine Antwort liegt,
   * ihre Startwerte aus dem Template (Mondphase, Warnschild).
   */
  private static hatEigeneWerte(werte?: {[boxId: string]: string}): boolean {
    if (!werte) {
      return false;
    }
    const schluessel = Object.keys(werte);
    return schluessel.length === 0 || schluessel.some(key => !ImageComponent.istFremd(key));
  }

  /** Übernimmt die vom Server gelieferten Werte in den Abschnitts-Cache (ohne HTTP-Aufruf). */
  private loadEmbeddedValues(): void {
    if (this.image?.values) {
      // Fremde Schlüssel bleiben draußen: sie sind keine Box-Werte und hätten in der
      // Zählung, in den Umschaltern und beim "leeren" nichts zu suchen.
      this.sectionValues.set(ImageComponent.DEFAULT_BUCKET,
        new Map(Object.entries(this.image.values).filter(([key]) => !ImageComponent.istFremd(key))));
    }
    for (const section of this.image?.sections ?? []) {
      if (section.values) {
        this.sectionValues.set(this.sectionKey(section), new Map(Object.entries(section.values)));
      }
    }
    this.ladeUmschalter();
    this.applyActiveSection();
  }

  /** Aktuelles Hintergrundbild: Bild des aktiven Abschnitts, sonst das Basis-Bild. */
  get currentImageUrl(): string | undefined {
    return this.activeSection?.imageUrl ?? this.image?.imageUrl;
  }

  /**
   * Boxen des aktiven Abschnitts: eigene Section-Boxen (z.B. Prognose),
   * sonst bei Zeiträumen/ohne Abschnitt die Bild-Boxen (Städte). Bei Graph-Abschnitten keine.
   */
  get activeBoxes(): ImageBox[] {
    if (this.activeSection?.graph) {
      return [];
    }
    let boxes: ImageBox[];
    if (this.activeSection?.boxes) {
      boxes = this.activeSection.boxes;
    } else if (!this.activeSection || this.activeSection.tempPrefix) {
      boxes = this.image?.boxes ?? [];
    } else {
      return [];
    }
    // Boxen können auf bestimmte Abschnitte beschränkt sein (z.B. Mondphase nur "heute Nacht").
    return boxes.filter(box => !box.sections
      || (this.activeSectionId != null && box.sections.includes(this.activeSectionId)));
  }

  /**
   * Statische Beschriftungen: die des aktiven Abschnitts, sonst die des Bildes selbst
   * (z.B. "ganz NRW" auf Karten ohne Zeitraum-Sidebar).
   */
  get activeLabels(): ImageLabel[] {
    return this.activeSection?.labels ?? this.image?.labels ?? [];
  }

  /** Liniendiagramm-Konfiguration: die des aktiven Abschnitts, sonst die des Bildes (Tendenz). */
  get activeGraph(): ImageGraph | undefined {
    return this.activeSection?.graph ?? this.image?.graph;
  }

  /**
   * Messwertdatei für den Selbst-Abruf des Diagramms – nur bei einem Abschnitts-Graphen
   * (Grafik-Workflows, Tendenz nutzt eine eigene Datei).
   *
   * Für einen Graphen am Bild (`Image.graph`, Lokalzeit "Tendenz") bewusst `undefined`:
   * die Diagramm-Komponente holt sich mit einer Datei die Werte schon in `ngOnChanges`,
   * also beim Seitenaufbau – die Lokalzeit-Karten sollen aber leer starten und erst mit
   * "MSWR-MOS laden" gefüllt werden (`ladeTendenz()` liefert dann fertige Punkte).
   */
  get activeGraphFile(): string | undefined {
    if (!this.activeSection?.graph) {
      return undefined;
    }
    return this.activeSection.measurementsFile ?? this.image?.measurementsFile;
  }

  /** Position/Größe/Farbe einer statischen Beschriftung (proportional, mittelpunktzentriert). */
  labelStyle(label: ImageLabel): { [key: string]: string } {
    return {
      left: (label.x / this.REF_WIDTH * 100) + '%',
      top: (label.y / this.REF_HEIGHT * 100) + '%',
      width: (label.width / this.REF_WIDTH * 100) + '%',
      height: (label.height / this.REF_HEIGHT * 100) + '%',
      'font-size': ((label.fontSize ?? 40) / this.REF_WIDTH * 100) + 'cqw',
      'background-color': label.backgroundColor ?? 'transparent',
      color: label.fontColor ?? '#ffffff'
    };
  }

  /**
   * Text einer Beschriftung: fester Text, sonst Wochentag (heute + weekdayOffset).
   * Der feste Text hat Vorrang, und weekdayOffset zählt nur, wenn wirklich ein Wert
   * gesetzt ist – der Server liefert das Feld auch als explizites `null` mit, was
   * sonst fälschlich als Wochentags-Label interpretiert würde.
   */
  labelText(label: ImageLabel): string {
    if (label.text) {
      return label.text;
    }
    if (label.weekdayOffset !== undefined && label.weekdayOffset !== null) {
      return ImageComponent.weekday(label.weekdayOffset);
    }
    return '';
  }

  /**
   * Wochentagskürzel für heute + offset (wie meteoeffects WDRUtils.getWeekday).
   *
   * ⚠️ `getDay()` zählt ab Sonntag (0), die Liste beginnt mit Montag – deshalb `- 1`, wie in
   * `MeteoLineGraphComponent.weekday`. Ohne die Korrektur standen alle Wochentags-Labels der
   * Prognose-Karte einen Tag zu spät (an einem Freitag „So Mo Di" statt „Sa So Mo"), was die
   * Zuordnung der Prognose-Spalten zu den Aussichtentagen falsch aussehen ließ (Oktober 2026).
   */
  static weekday(offset: number): string {
    const weekdays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return weekdays[((new Date().getDay() - 1 + offset) % 7 + 7) % 7];
  }

  /** Klick auf einen Sidebar-Eintrag: Bild umschalten und Box-Werte des Zeitraums anzeigen. */
  onSectionClick(section: ImageSection): void {
    this.activeSection = section;
    this.activeSectionId = this.sectionKey(section);
    this.applyActiveSection();
  }

  private sectionKey(section: ImageSection): string {
    return section.id ?? section.title;
  }

  private get activeKey(): string {
    return this.activeSection ? this.sectionKey(this.activeSection) : ImageComponent.DEFAULT_BUCKET;
  }

  /**
   * MOS-Schlüssel des Symbol-Anteils einer Kombi-Box.
   *
   * In einem Abschnitt mit Präfixen (Lokalzeit-Prototyp) ergibt er sich aus `iconPrefix`
   * plus Stadt, sonst steht er fest in `iconDataKey`. Für Boxen ohne Symbol-Anteil
   * liefert die Methode nichts.
   */
  private iconKeyFor(box: ImageBox, section?: ImageSection): string | undefined {
    if (!this.istKombi(box)) {
      return undefined;
    }
    if (section?.iconPrefix && box.city) {
      return section.iconPrefix + box.city;
    }
    return box.iconDataKey;
  }

  /** Effektiver MOS-Schlüssel einer Box: Abschnitts-Präfix + Stadt, sonst der feste dataKey. */
  private boxKeyFor(box: ImageBox, section?: ImageSection): string | undefined {
    if (section && box.city) {
      const prefix = box.type === 'temperature' ? section.tempPrefix : section.iconPrefix;
      if (prefix) {
        return prefix + box.city;
      }
    }
    return box.dataKey;
  }

  /**
   * Holt die Punkte eines Bild-Graphen (`Image.graph`) aus der Tendenz-Datei des Bildes.
   *
   * Der Graph wird als **neues Objekt** gesetzt: die Diagramm-Komponente bekommt ihn als
   * Input und lädt nur in `ngOnChanges` – dieselbe Referenz mit geänderten `points` sähe
   * sie nicht. Die Punkte werden nicht gespeichert (wie bisher im Grafik-Workflows-Graph).
   */
  private ladeTendenz(karte: string, umfang?: GrafikUmfang): void {
    const graph = this.image?.graph;
    const file = this.image?.measurementsFile;
    if (!graph || !file) {
      console.debug(`MSWR-MOS laden: ${karte} Tendenz übersprungen – keine Tendenz-Datei hinterlegt`);
      return;
    }
    this.measurementsService.getMeasurements(file).pipe(take(1)).subscribe({
      next: values => {
        this.ladeQuellenService.melde(umfang, {art: 'mos', name: file, stand: this.measurementsService.stand(file)});
        const points = MeteoLineGraphComponent.punkteAus(graph, values, k => this.measurementsService.normalizeKey(k));
        this.image!.graph = {...graph, points};
        this.markiereGeaendert();
        console.debug(`MSWR-MOS laden: ${karte} Tendenz fertig – ${points.length} Punkte aus ${file}`);
      },
      error: err => this.meldeLadefehler(`Tendenz-Datei ${file} für ${karte} nicht ladbar `
        + `(Status ${err?.status}${err?.statusText ? ' ' + err.statusText : ''})`)
    });
  }

  /** Lädt die Messwertdatei einmalig und baut den Wert-Cache je Abschnitt auf. */
  private loadMeasurements(umfang?: GrafikUmfang): void {
    const file = this.image?.measurementsFile;
    const boxes = this.image?.boxes;
    const karte = this.image?.containerId ?? this.image?.caption ?? '(ohne Kennung)';
    if (!file || !boxes || boxes.length === 0) {
      console.debug(`MSWR-MOS laden: ${karte} übersprungen – `
        + `${!file ? 'keine Messwertdatei' : 'keine Boxen'} hinterlegt`);
      return;
    }
    const erwartet = this.allBoxes().filter(box => this.boxKeyFor(box, this.activeSection)).length;
    console.debug(`MSWR-MOS laden: ${karte} fragt ${file} für ${erwartet} von `
      + `${this.allBoxes().length} Boxen an`);

    this.measurementsService.getMeasurements(file).pipe(take(1)).subscribe({
      next: values => {
        this.ladeQuellenService.melde(umfang, {art: 'mos', name: file, stand: this.measurementsService.stand(file)});
        this.measurements = values;
        this.buildSectionValues();     // spiegelt selbst in image.values, also speicherbar
        this.applyActiveSection();
        this.markiereGeaendert();

        const gefuellt = Object.values(this.image?.values ?? {})
          .filter(wert => ImageComponent.istWert(wert)).length;
        console.debug(`MSWR-MOS laden: ${karte} fertig – ${gefuellt} Werte eingetragen `
          + `(Datei hat ${values.size} Schlüssel)`);
        if (erwartet === 0) {
          // Kein einziger Box-Schlüssel: für diese Zeile gibt die Datei nichts her. Das ist
          // bei "Heute" und den Aussichtentagen so gewollt – die MOS-Dateien kennen nur
          // heute Nacht, morgen Vormittag und morgen Nachmittag.
          console.debug(`MSWR-MOS laden: ${karte} hat keine MOS-Schlüssel – für diese Zeile `
            + `enthält ${file} keine Werte, die Boxen bleiben leer.`);
          // ... und das soll der Redakteur auch sehen, nicht nur das Log: die Zeile öffnet
          // standardmäßig mit "Heute", und dort tat der Knopf scheinbar nichts. Gebündelt
          // wird beim Empfänger, weil ein Knopfdruck viele Karten trifft.
          this.eventBusService.emit(EventBusTypes.MOS_OHNE_WERTE_EVENT.valueOf(),
            new MosOhneWerte(karte, this.zeilenName(), file));
        }
        // Nichts gefüllt, obwohl Schlüssel erwartet wurden: dann passen Schlüssel und
        // Datei nicht zusammen. Das ist ein echter Fehler und keine Ablaufspur.
        if (gefuellt === 0 && erwartet > 0) {
          this.meldeLadefehler(`MOS-Datei ${file} geladen (${values.size} Schlüssel), aber `
            + `keiner der ${erwartet} Box-Schlüssel von ${karte} kommt darin vor. `
            + `Gesucht wurde z.B. "${this.fehlendeSchluessel().slice(0, 3).join('", "')}".`);
        } else {
          this.meldeFehlendeSchluessel(karte, file, values.size);
        }
      },
      error: err => this.meldeLadefehler(`MOS-Datei ${file} für ${karte} nicht ladbar `
        + `(Status ${err?.status}${err?.statusText ? ' ' + err.statusText : ''})`
        + `${err?.message ? ': ' + err.message : ''}`)
    });
  }

  /** Zeilenüberschrift dieser Karte: "HEUTE: ganz - Karte 1" -> "HEUTE: ganz". */
  private zeilenName(): string {
    const caption = this.image?.caption ?? '';
    const trenner = caption.indexOf(' - Karte');
    return trenner > 0 ? caption.substring(0, trenner) : caption;
  }

  /** Box-Schlüssel, die in der geladenen Datei nicht vorkommen. */
  private fehlendeSchluessel(): string[] {
    const fehlend: string[] = [];
    for (const box of this.allBoxes()) {
      for (const schluessel of [this.boxKeyFor(box, this.activeSection),
                                this.iconKeyFor(box, this.activeSection),
                                this.isMaxMinIcon(box) ? box.minDataKey : undefined]) {
        if (schluessel && !this.measurements.has(this.measurementsService.normalizeKey(schluessel))) {
          fehlend.push(schluessel);
        }
      }
    }
    return fehlend;
  }

  /** Meldet einzelne Schlüssel, die die Datei nicht kennt – die Box bleibt dann leer. */
  private meldeFehlendeSchluessel(karte: string, file: string, anzahl: number): void {
    const fehlend = this.fehlendeSchluessel();
    if (fehlend.length === 0) {
      return;
    }
    this.meldeLadefehler(`MOS-Datei ${file} (${anzahl} Schlüssel) kennt `
      + `${fehlend.length} Schlüssel von ${karte} nicht: "${fehlend.slice(0, 5).join('", "')}"`
      + `${fehlend.length > 5 ? ' …' : ''}. Diese Boxen bleiben leer.`);
  }

  /** Baut je Abschnitt (bzw. Standard-Bucket) die Box-Werte aus der geladenen Datei. */
  private buildSectionValues(): void {
    const sections = this.image?.sections ?? [];
    if (sections.length === 0) {
      this.sectionValues.set(ImageComponent.DEFAULT_BUCKET, this.bucketFor(this.image?.boxes ?? [], undefined));
      this.spiegleInsDokument();
      return;
    }
    for (const section of sections) {
      if (section.graph) {
        continue; // Graph-Abschnitte laden ihre Daten selbst
      }
      const boxes = section.boxes ?? this.image?.boxes ?? [];
      this.sectionValues.set(this.sectionKey(section), this.bucketFor(boxes, section));
    }
    this.spiegleInsDokument();
  }

  /**
   * Ist das ein echter Wert?
   *
   * ⚠️ `null` muss mitgeprüft werden, nicht nur `undefined`: der Rest-Service serialisiert
   * nicht gesetzte Felder als **`null`** (`"value": null`), und `null !== undefined` ist in
   * JavaScript wahr. Eine Prüfung nur auf `undefined` legte deshalb für **jede** Box einen
   * `null`-Eintrag im Wert-Cache ab – die Box blieb leer, die Zählung meldete aber einen
   * Wert, und ein bereits vorhandener Wert wurde nicht mehr als solcher erkannt.
   */
  private static istWert(wert: string | null | undefined): boolean {
    return wert !== undefined && wert !== null && wert !== '';
  }

  /** Wert-Bucket (BoxId -> Wert) für eine Boxliste im Kontext eines Abschnitts. */
  private bucketFor(boxes: ImageBox[], section?: ImageSection): Map<string, string> {
    const bucket = new Map<string, string>();
    for (const box of boxes) {
      const dataKey = this.boxKeyFor(box, section);
      let value = dataKey ? this.measurements.get(this.measurementsService.normalizeKey(dataKey)) : undefined;
      // Boxen ohne MOS-Schlüssel (Mondphase/Gefahr) mit ihrem Startwert (Icon-Index) vorbelegen.
      if (!ImageComponent.istWert(value)) {
        value = box.value;
      }
      // Was schon in der Box steht, bleibt stehen: ein Neuaufbau des Caches darf weder die
      // Startwerte aus dem Template noch eine Eingabe des Redakteurs löschen.
      if (!ImageComponent.istWert(value) && box.label) {
        value = this.sanitize(box.label);
      }
      if (ImageComponent.istWert(value)) {
        bucket.set(box.id, value as string);
      }
      // Symbol-Anteil einer Kombi-Box: eigener MOS-Schlüssel, sonst der bereits
      // angezeigte bzw. der Startwert aus dem Template.
      const iconSchluessel = this.iconKeyFor(box, section);
      let iconWert = iconSchluessel
        ? this.measurements.get(this.measurementsService.normalizeKey(iconSchluessel))
        : undefined;
      if (!ImageComponent.istWert(iconWert)) {
        iconWert = box.iconLabel || box.iconValue;
      }
      if (ImageComponent.istWert(iconWert)) {
        bucket.set(box.id + ImageComponent.ICON_KEY_SUFFIX, this.sanitize(iconWert as string));
      }
      // Tiefstwert einer Höchst-/Tiefstwert-Box: eigener MOS-Schlüssel, sonst der angezeigte
      // bzw. der Startwert – wie der Symbol-Anteil.
      if (this.isMaxMinIcon(box)) {
        let minWert = box.minDataKey
          ? this.measurements.get(this.measurementsService.normalizeKey(box.minDataKey))
          : undefined;
        if (!ImageComponent.istWert(minWert)) {
          minWert = box.minLabel || box.minValue;
        }
        if (ImageComponent.istWert(minWert)) {
          bucket.set(box.id + ImageComponent.MIN_KEY_SUFFIX, this.sanitize(minWert as string));
        }
      }
      // Böen und Richtung einer Wind-Box haben keinen MOS-Schlüssel, stehen aber womöglich
      // schon in der Box – vom Stationsweg, der auf einer Nacht/Morgen-Karte neben der Datei
      // läuft und auch vor ihr antworten kann. Ein Neuaufbau des Caches darf sie nicht löschen.
      if (this.isWind(box)) {
        if (ImageComponent.istWert(box.gustLabel)) {
          bucket.set(box.id + ImageComponent.GUST_KEY_SUFFIX, this.sanitize(box.gustLabel as string));
        }
        if (ImageComponent.istWert(box.dirLabel)) {
          bucket.set(box.id + ImageComponent.DIR_KEY_SUFFIX, this.sanitize(box.dirLabel as string));
        }
      }
    }
    return bucket;
  }

  /** Überträgt die Werte des aktiven Abschnitts in die Box-Labels. */
  private applyActiveSection(): void {
    const bucket = this.sectionValues.get(this.activeKey);
    for (const box of this.activeBoxes) {
      if (!this.darfSchreiben(box)) {
        continue;   // gerade in Bearbeitung – Eingabe nicht überschreiben
      }
      const value = bucket?.get(box.id);
      box.label = (value ?? '') + this.unit(box);
      if (this.istKombi(box)) {
        // Der Icon-Anteil hat seinen eigenen Schlüssel; ersatzweise gilt der Startwert.
        box.iconLabel = bucket?.get(box.id + ImageComponent.ICON_KEY_SUFFIX) ?? box.iconValue ?? '';
      }
      if (this.isMaxMinIcon(box)) {
        box.minLabel = bucket?.get(box.id + ImageComponent.MIN_KEY_SUFFIX) ?? box.minValue ?? '';
      }
      if (this.isWind(box)) {
        box.gustLabel = bucket?.get(box.id + ImageComponent.GUST_KEY_SUFFIX) ?? box.gustValue ?? '';
        box.dirLabel = bucket?.get(box.id + ImageComponent.DIR_KEY_SUFFIX) ?? box.dirValue ?? '';
      }
    }
  }

  /**
   * Position/Größe/Schriftgröße einer Box – vollständig proportional zum Bild.
   * left/top/width/height in Prozent (Box wird per translate(-50%,-50%) auf ihren
   * Mittelpunkt zentriert), Schriftgröße in cqw (Container-Query-Breite), damit alles
   * mit dem Bild skaliert – unabhängig von Zoom/Fenstergröße.
   */
  boxStyle(box: ImageBox): { [key: string]: string } {
    return {
      left: (box.x / this.REF_WIDTH * 100) + '%',
      top: (box.y / this.REF_HEIGHT * 100) + '%',
      width: (box.width / this.REF_WIDTH * 100) + '%',
      height: (box.height / this.REF_HEIGHT * 100) + '%',
      'font-size': ((box.fontSize ?? 55) / this.REF_WIDTH * 100) + 'cqw',
      color: box.fontColor ?? '#363b4a'
    };
  }

  /** Einheit einer Box: Temperatur -> °, 'value'-Boxen ihre freie Einheit (km/h, %, h), sonst keine. */
  unit(box: ImageBox): string {
    if (this.hatTemperatur(box)) {
      return this.TEMPERATURE_UNIT;
    }
    return box.unit ?? '';
  }

  /** Hintergrundfarbe einer Box: Temperatur-Palette, helles Grau für 'value', sonst Standard. */
  boxBackground(box: ImageBox): string | null {
    if (this.istKombi(box) || this.isWind(box)) {
      return null;   // die Zellen bringen ihren eigenen Hintergrund mit
    }
    if (box.type === 'temperature') {
      return this.tempColor(box);
    }
    if (box.type === 'value') {
      return '#edecec';
    }
    if (this.isBoeen(box)) {
      return '#575757';   // dunkler Kasten mit heller Zahl, wie in der Vorlage der Redaktion
    }
    return null;
  }

  /**
   * Hintergrundfarbe einer Temperatur-Box abhängig vom aktuellen Wert
   * (Farblogik/Palette wie meteoeffects updateTempColor), leicht transparent (~80%).
   * Gibt null zurück, wenn keine Temperatur-Box oder kein gültiger Wert vorliegt.
   */
  tempColor(box: ImageBox): string | null {
    if (box.type !== 'temperature') {
      return null;
    }
    const value = this.numericPart(box);
    if (!/^-?\d+$/.test(value)) {
      return null;
    }
    // '#RRGGBB' + 'CC' (0xCC = 204 ≈ 80% Deckkraft)
    return this.tempHex(parseInt(value, 10)) + 'CC';
  }

  /** Temperatur -> Hex-Farbe (Palette aus meteoeffects wdr/temps.js). */
  tempHex(temp: number): string {
    if (temp <= -15) return '#75165B';
    if (temp <= -10) return '#00417B';
    if (temp <= -5) return '#006E9F';
    if (temp <= 0) return '#28A5A7';
    if (temp <= 5) return '#6AB165';
    if (temp <= 12) return '#A0C300';
    if (temp <= 20) return '#F49500';
    if (temp <= 25) return '#F26D00';
    if (temp <= 30) return '#E74100';
    return '#E90000';
  }

  /**
   * Hintergrund-Icon einer 'icon'-Box, abgeleitet aus dem aktuellen (numerischen) Wert.
   * Ändert sich der Wert, ändert sich automatisch das Icon.
   * Gibt 'none' zurück, wenn kein gültiger Icon-Code vorliegt oder keine Icon-Konfiguration existiert.
   */
  iconBgImage(box: ImageBox): string {
    if (box.type !== 'icon' && box.type !== 'danger') {
      return 'none';
    }
    // Box-eigene Icon-Konfiguration (z.B. Mond/Gefahr) hat Vorrang vor der des Bildes.
    const folder = box.iconFolder ?? this.image?.iconFolder;
    if (!folder) {
      return 'none';
    }
    const value = this.numericPart(box);
    if (!/^\d+$/.test(value)) {
      return 'none';
    }
    const prefix = box.iconPrefix ?? this.image?.iconPrefix ?? '';
    const ext = box.iconExtension ?? this.image?.iconExtension ?? '';
    return `url("${folder}${prefix}${value}${ext}")`;
  }

  /** Outline-Farbe je Box-Typ (wie in meteoeffects getOutlineColor). */
  outlineColor(box: ImageBox): string {
    switch (box.type) {
      case 'icon':
        return 'rgb(148,0,255)';      // lila
      case 'value':
        return 'rgb(160,160,160)';    // grau (Wind/Regen/Sonne)
      case 'danger':
        return 'rgb(255,128,0)';      // orange
      case ImageComponent.TYPE_TEMP_ICON:
      case ImageComponent.TYPE_MAX_MIN_ICON:
        return 'rgb(0,150,255)';      // blau (Kombi aus Temperatur(en) + Symbol)
      case ImageComponent.TYPE_WIND:
        return 'rgb(31,59,115)';      // dunkelblau (Wind)
      case 'temperature':
      default:
        return 'rgb(0,255,222)';      // cyan
    }
  }

  /**
   * Maus betritt eine Box: hervorheben.
   *
   * Den Regions-Hinweis blendet CSS ein, verzögert per {@code transition-delay} –
   * hier ist dafür nichts zu tun. Vorher hing er an einem {@code setTimeout}, das
   * eine {@code @if}-Bedingung umschaltete; Angular bewertete die im
   * Verifikationsdurchlauf anders als im Hauptdurchlauf, warf NG0100 und brach das
   * Rendern ab, sodass der Hinweis nie erschien.
   */
  onBoxEnter(box: ImageBox): void {
    this.hoveredId = box.id;
  }

  /** Maus verlässt die Box: Hervorhebung zurücknehmen. */
  onBoxLeave(): void {
    this.hoveredId = null;
  }

  ngOnDestroy(): void {
    this.destroyer$.next(true);
    this.destroyer$.complete();
    this.beendePfeilZiehen();
    this.pfeilAbwaehlen();
  }

  // ------------------------------------------------------------------
  // Verbindungs-Pfeile (Oktober 2026)
  // ------------------------------------------------------------------

  /**
   * Faktor des Pfeil-Griffs gegenüber den Umschaltern der Box. Zunächst 30 % kleiner (0,7),
   * auf Wunsch der Redaktion zweimal um die Hälfte vergrößert (0,7 × 1,5 × 1,5) – samt
   * Greiffläche (`::before` im SCSS).
   */
  private static readonly PFEIL_GRIFF_GROESSE = 1.575;
  /** Länge der Pfeilspitze im Referenzraum (Marker in userSpaceOnUse). */
  private static readonly PFEIL_SPITZE = 28;
  /** Ab dieser Bewegung (Referenzraum) gilt ein Pointer-Down als Ziehen, nicht als Klick. */
  private static readonly PFEIL_KLICK_TOLERANZ = 6;

  /** Liest die Pfeile der Karte aus `image.values` (`ki:pfeile`). */
  private ladePfeile(): void {
    this.pfeile = KiGrafikService.pfeileVon(this.image);
  }

  /**
   * Schreibt die Pfeile als `ki:pfeile` in `image.values` – direkt, nicht über den
   * Wert-Cache: ein fremder Schlüssel, den {@link spiegleInsDokument} nur durchreicht, wie
   * `ki:temp`. Ohne Pfeile verschwindet der Schlüssel.
   */
  private schreibePfeile(): void {
    if (!this.image) {
      return;
    }
    const werte = {...(this.image.values ?? {})};
    const text = KiGrafikService.pfeileAlsText(this.pfeile);
    if (text === undefined) {
      if (!(KiGrafikService.PFEILE_SCHLUESSEL in werte)) {
        return;
      }
      delete werte[KiGrafikService.PFEILE_SCHLUESSEL];
    } else {
      werte[KiGrafikService.PFEILE_SCHLUESSEL] = text;
    }
    this.image.values = werte;
  }

  /** Nimmt alle Pfeile weg, die von dieser Box ausgehen oder auf sie zeigen. */
  private entfernePfeileVon(boxId: string): void {
    let geaendert = false;
    for (const [von, nach] of [...this.pfeile.entries()]) {
      if (von === boxId || nach === boxId) {
        this.pfeile.delete(von);
        geaendert = true;
      }
    }
    if (geaendert) {
      this.schreibePfeile();
    }
  }

  /**
   * Zeigt diese Box den Halbkreis-Griff für einen Verbindungs-Pfeil? Nur Symbol-, Kombi-
   * und Wind-Boxen, und nur solange ihr gelbes Kästchen angehakt ist – was nicht in die
   * KI-Anfrage geht, kann auch nicht verbunden werden, und die Oberfläche bleibt ruhig.
   */
  zeigtPfeilGriff(box: ImageBox): boolean {
    if (!KiGrafikService.kannPfeilTragen(box)) {
      return false;
    }
    const gelb = (box.toggles ?? []).find(t => t.id === KiGrafikService.KI_UMSCHALTER);
    return !!gelb && this.isToggleActive(box, gelb);
  }

  /** Hat diese Box einen ausgehenden Pfeil? */
  hatPfeil(box: ImageBox): boolean {
    return this.pfeile.has(box.id);
  }

  /** Wird gerade ein Pfeil von dieser Box gezogen? (Griff zeigt den Greif-Zustand) */
  ziehtPfeilVon(box: ImageBox): boolean {
    return this.pfeilZiehen?.von.id === box.id;
  }

  /**
   * Klick auf einen Pfeil: wählt ihn aus (bzw. wieder ab) – der ausgewählte Pfeil zeigt in
   * seiner Mitte ein X, das ihn entfernt. Ein Klick irgendwo sonst hebt die Auswahl auf.
   */
  onPfeilKlick(event: Event, von: string): void {
    event.stopPropagation();
    if (this.pfeilAusgewaehlt === von) {
      this.pfeilAbwaehlen();
    } else {
      this.pfeilAusgewaehlt = von;
      if (!this.pfeilAuswahlListener) {
        const ab = (e: Event) => {
          const ziel = e.target as Element | null;
          if (ziel?.closest?.('.meteo-pfeile__treffer, .meteo-pfeile__loeschen')) {
            return;   // Klick auf einen Pfeil oder sein X – das regeln deren eigene Handler
          }
          this.pfeilAbwaehlen();
          this.markiereGeaendert();
        };
        document.addEventListener('pointerdown', ab);
        this.pfeilAuswahlListener = () => document.removeEventListener('pointerdown', ab);
      }
    }
    this.markiereGeaendert();
  }

  /** Das X am ausgewählten Pfeil: entfernt ihn. */
  loeschePfeil(event: Event, von: string): void {
    event.stopPropagation();
    if (this.pfeile.delete(von)) {
      this.schreibePfeile();
    }
    this.pfeilAbwaehlen();
    this.markiereGeaendert();
  }

  private pfeilAbwaehlen(): void {
    this.pfeilAusgewaehlt = null;
    this.pfeilAuswahlListener?.();
    this.pfeilAuswahlListener = null;
  }

  /** Position des X in der Mitte eines Pfeils, in Prozent der Karte. */
  loeschKnopfStil(linie: {x1: number; y1: number; x2: number; y2: number}): { [key: string]: string } {
    return {
      left: ((linie.x1 + linie.x2) / 2 / this.REF_WIDTH * 100) + '%',
      top: ((linie.y1 + linie.y2) / 2 / this.REF_HEIGHT * 100) + '%'
    };
  }

  /** Größe des Griffs: 70 % der Umschalter der Box (wie `toggleStyle`, in em der Box-Schrift). */
  pfeilGriffStil(box: ImageBox): { [key: string]: string } {
    return {'font-size': (box.toggleScale ?? 1) * ImageComponent.PFEIL_GRIFF_GROESSE + 'em'};
  }

  /** Hover-Text des Griffs: wozu er dient und wie man ihn bedient. */
  pfeilTitel(box: ImageBox): string {
    const was = this.isWind(box) ? 'den Wind' : 'das Wetter-Symbol';
    const loeschen = this.hatPfeil(box) ? ' Ein Klick entfernt den Pfeil dieser Box.' : '';
    return `Verbindungs-Pfeil für den KI-Text: Griff gedrückt halten und auf einer anderen angehakten Box `
      + `dieser Karte loslassen. Die KI nennt die verbundenen Regionen dann als Weg in dieser Richtung `
      + `("vom … über … bis …") – für ${was}, nicht für die Temperatur.${loeschen}`;
  }

  /** Eindeutige Id der Pfeilspitze dieser Karte (ein SVG-Marker je Karte, viele Karten je Seite). */
  get pfeilMarkerId(): string {
    return 'meteo-pfeilspitze-' + (this.image?.containerId ?? '').replace(/[^A-Za-z0-9_-]/g, '_');
  }

  /**
   * Die Pfeile als Linien im Referenzraum (1920×1080): von der Kante der Quell-Box zur Kante
   * der Ziel-Box, entlang der Verbindung der Mittelpunkte; die Spitze endet an der Zielkante.
   * Pfeile, deren Box nicht (mehr) auf der Karte liegt oder nicht angehakt ist, bleiben unsichtbar.
   */
  pfeilLinien(): {von: string; nach: string; x1: number; y1: number; x2: number; y2: number}[] {
    const boxen = new Map(this.activeBoxes.map(box => [box.id, box]));
    const linien: {von: string; nach: string; x1: number; y1: number; x2: number; y2: number}[] = [];
    this.pfeile.forEach((nachId, vonId) => {
      const von = boxen.get(vonId);
      const nach = boxen.get(nachId);
      if (!von || !nach || !this.zeigtPfeilGriff(von) || !this.zeigtPfeilGriff(nach)) {
        return;
      }
      const linie = ImageComponent.linieZwischen(von, nach);
      if (linie) {
        linien.push({von: vonId, nach: nachId, ...linie});
      }
    });
    return linien;
  }

  /** Die Linie des Pfeils, der gerade gezogen wird: vom Griff der Quell-Box zum Zeiger. */
  pfeilEntwurf(): {x1: number; y1: number; x2: number; y2: number} | null {
    const z = this.pfeilZiehen;
    if (!z || !z.bewegt) {
      return null;
    }
    return {x1: z.von.x, y1: z.von.y + z.von.height / 2, x2: z.x, y2: z.y};
  }

  /** Kante-zu-Kante-Strecke zweier Boxen entlang ihrer Mittelpunkte; null, wenn sie sich überdecken. */
  private static linieZwischen(von: ImageBox, nach: ImageBox):
    {x1: number; y1: number; x2: number; y2: number} | null {
    const dx = nach.x - von.x;
    const dy = nach.y - von.y;
    const laenge = Math.hypot(dx, dy);
    if (laenge === 0) {
      return null;
    }
    const austritt = (box: ImageBox): number => Math.min(
      dx === 0 ? Infinity : (box.width / 2) / Math.abs(dx),
      dy === 0 ? Infinity : (box.height / 2) / Math.abs(dy));
    const tVon = austritt(von);
    const tNach = austritt(nach);
    if (tVon + tNach >= 1) {
      return null;
    }
    const abstand = 4 / laenge;   // etwas Luft zwischen Spitze und Zielkante
    return {
      x1: von.x + dx * tVon, y1: von.y + dy * tVon,
      x2: nach.x - dx * (tNach + abstand), y2: nach.y - dy * (tNach + abstand)
    };
  }

  /**
   * Pointer-Down auf dem Griff: beginnt das Ziehen. Die Bewegung wird am Dokument
   * verfolgt, damit der Pfeil auch außerhalb der Box weiterläuft; Pointer-Up entscheidet,
   * ob ein Ziel getroffen wurde (Ziehen) oder ob es nur ein Klick war (Pfeil der Box weg).
   */
  onPfeilStart(event: PointerEvent, box: ImageBox): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.pfeilZiehen) {
      return;
    }
    this.pfeilAbwaehlen();
    const punkt = this.referenzPunkt(event);
    this.pfeilZiehen = {von: box, x: punkt.x, y: punkt.y, bewegt: false};
    const move = (e: PointerEvent) => this.onPfeilBewegung(e);
    const up = (e: PointerEvent) => this.onPfeilEnde(e);
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
    this.pfeilListener = [
      () => document.removeEventListener('pointermove', move),
      () => document.removeEventListener('pointerup', up),
      () => document.removeEventListener('pointercancel', up)
    ];
    this.markiereGeaendert();
  }

  private onPfeilBewegung(event: PointerEvent): void {
    const z = this.pfeilZiehen;
    if (!z) {
      return;
    }
    const punkt = this.referenzPunkt(event);
    const start = {x: z.von.x, y: z.von.y + z.von.height / 2};
    z.x = punkt.x;
    z.y = punkt.y;
    if (!z.bewegt && Math.hypot(punkt.x - start.x, punkt.y - start.y) > ImageComponent.PFEIL_KLICK_TOLERANZ) {
      z.bewegt = true;
    }
    this.markiereGeaendert();
  }

  private onPfeilEnde(event: PointerEvent): void {
    const z = this.pfeilZiehen;
    this.beendePfeilZiehen();
    if (!z) {
      return;
    }
    if (!z.bewegt) {
      // Klick ohne Ziehen: der ausgehende Pfeil dieser Box kommt weg.
      if (this.pfeile.delete(z.von.id)) {
        this.schreibePfeile();
      }
      this.markiereGeaendert();
      return;
    }
    const ziel = this.boxUnterZeiger(event);
    if (ziel && ziel.id !== z.von.id && this.zeigtPfeilGriff(ziel)) {
      this.verbinde(z.von, ziel);
    }
    this.markiereGeaendert();
  }

  /** A → B setzen; ein Gegenpfeil B → A widerspräche und fällt weg. */
  private verbinde(von: ImageBox, nach: ImageBox): void {
    if (this.pfeile.get(nach.id) === von.id) {
      this.pfeile.delete(nach.id);
    }
    this.pfeile.set(von.id, nach.id);
    this.schreibePfeile();
  }

  private beendePfeilZiehen(): void {
    this.pfeilListener.forEach(ab => ab());
    this.pfeilListener = [];
    this.pfeilZiehen = null;
  }

  /** Die Box dieser Karte unter dem Zeiger (über `data-box-id`), sonst null – andere Karten zählen nicht. */
  private boxUnterZeiger(event: PointerEvent): ImageBox | null {
    const element = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-box-id]');
    if (!element || !this.host.nativeElement.contains(element)) {
      return null;
    }
    const id = element.getAttribute('data-box-id');
    return this.activeBoxes.find(box => box.id === id) ?? null;
  }

  /** Zeigerposition in Koordinaten des Referenzraums der Karte. */
  private referenzPunkt(event: PointerEvent): {x: number; y: number} {
    const flaeche = this.host.nativeElement.querySelector('.meteo-image') as HTMLElement | null;
    const rect = flaeche?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      return {x: 0, y: 0};
    }
    return {
      x: (event.clientX - rect.left) / rect.width * this.REF_WIDTH,
      y: (event.clientY - rect.top) / rect.height * this.REF_HEIGHT
    };
  }

  /** Zeigt diese Box ein Wetter-Symbol? Dann wird die Zahl mit einem Kreis hinterlegt. */
  isIconBox(box: ImageBox): boolean {
    return box.type === 'icon' || box.type === 'danger';
  }

  /** Unwetter-Box (Warnschild)? Dort sitzt die Zahl oben rechts, nicht mittig. */
  isDangerBox(box: ImageBox): boolean {
    return box.type === 'danger';
  }

  /** Ist das eine Wind-Box (Mittelwind/Böen plus Windpfeil)? */
  isWind(box: ImageBox): boolean {
    return box.type === ImageComponent.TYPE_WIND;
  }

  isBoeen(box: ImageBox): boolean {
    return box.type === ImageComponent.TYPE_BOEEN;
  }

  /** Numerischer Böen-Wert (unterer Kasten). */
  gustPart(box: ImageBox): string {
    return this.sanitize(box.gustLabel ?? '');
  }

  /** Numerischer Wert der Windrichtung (Feld in der Pfeilmitte). */
  dirPart(box: ImageBox): string {
    return this.sanitize(box.dirLabel ?? '');
  }

  /**
   * Drehwinkel des Windpfeils in Grad: (Zahl − 1) × 22,5. Der Pfeil zeigt, **wohin** der
   * Wind weht: 1 = Wind aus Süd -> Pfeil nach oben, 5 = aus West -> nach rechts,
   * 9 = aus Nord -> nach unten, 13 = aus Ost -> nach links.
   * Werte außerhalb 1..16 werden in den Bereich geklemmt.
   */
  windAngle(box: ImageBox): number {
    const raw = parseInt(this.dirPart(box), 10);
    if (isNaN(raw) || ImageComponent.DIR_ROTATING.includes(raw)) {
      return 0;   // der Kreispfeil hat keine Richtung
    }
    const clamped = Math.min(Math.max(raw, 1), ImageComponent.DIR_MAX);
    return (clamped - 1) * ImageComponent.DIR_STEP_DEGREES;
  }

  /** Wechselnde Richtungen (17 oder 18)? Dann ein Kreispfeil statt des Richtungspfeils. */
  isRotatingWind(box: ImageBox): boolean {
    return ImageComponent.DIR_ROTATING.includes(parseInt(this.dirPart(box), 10));
  }

  /** Himmelsrichtung als Text – dient als Titel des Windpfeils (Mouse-over). */
  windDirectionName(box: ImageBox): string {
    if (this.isRotatingWind(box)) {
      return ImageComponent.WIND_WECHSELND;
    }
    const zahl = parseInt(this.dirPart(box), 10);
    const name = ImageComponent.WIND_RICHTUNGEN.get(zahl);
    return name ? `Wind aus ${name}` : '';
  }

  /** Breite der Eingabefelder in den Wind-Kästen. */
  gustInputWidth(box: ImageBox): string {
    return Math.max(this.gustPart(box).length, 1) + 'ch';
  }

  dirInputWidth(box: ImageBox): string {
    return Math.max(this.dirPart(box).length, 1) + 'ch';
  }

  /** Eingabe im Böen-Kasten. */
  onGustInput(box: ImageBox, input: HTMLInputElement): void {
    box.gustLabel = this.writeBack(box, input, ImageComponent.GUST_KEY_SUFFIX);
  }

  /** Eingabe im Richtungsfeld – dreht damit auch den Pfeil. */
  onDirInput(box: ImageBox, input: HTMLInputElement): void {
    box.dirLabel = this.writeBack(box, input, ImageComponent.DIR_KEY_SUFFIX);
  }

  /** Bereinigt die Eingabe und merkt sie unter dem jeweiligen Zusatz-Schlüssel. */
  private writeBack(box: ImageBox, input: HTMLInputElement, suffix: string): string {
    const clean = this.sanitize(input.value);
    if (input.value !== clean) {
      input.value = clean;
    }
    this.rememberValue(box.id + suffix, clean);
    return clean;
  }

  /** Ist das eine Kombi-Box (Temperatur links, Wetter-Symbol rechts)? */
  isTempIcon(box: ImageBox): boolean {
    return box.type === ImageComponent.TYPE_TEMP_ICON;
  }

  /** Höchst-/Tiefstwert-Box: links Höchstwert über Tiefstwert, rechts das Wetter-Symbol. */
  isMaxMinIcon(box: ImageBox): boolean {
    return box.type === ImageComponent.TYPE_MAX_MIN_ICON;
  }

  /**
   * Kombi-Box mit Symbol-Anteil rechts (`tempicon`, `maxminicon`): das Symbol liegt unter
   * '<boxId>.icon', der Hauptwert ist eine Temperatur. Alles, was den Symbol-Anteil betrifft
   * (Stationsweg, MOS-Schlüssel, Sync, Abschnittswechsel), gilt für beide gleich.
   */
  istKombi(box: ImageBox): boolean {
    return this.isTempIcon(box) || this.isMaxMinIcon(box);
  }

  /** Trägt die Box als Hauptwert eine Temperatur (Einheit °, Stationsweg liefert sie)? */
  hatTemperatur(box: ImageBox): boolean {
    return box.type === 'temperature' || this.istKombi(box);
  }

  /** Nur der numerische Teil des Tiefstwerts (unterer Kasten der Höchst-/Tiefstwert-Box). */
  minPart(box: ImageBox): string {
    return this.sanitize(box.minLabel ?? '');
  }

  minInputWidth(box: ImageBox): string {
    return Math.max(this.minPart(box).length, 1) + 'ch';
  }

  /** Hintergrund des unteren Kastens: Temperaturfarbe zum Tiefstwert. */
  minCellBackground(box: ImageBox): string | null {
    const value = this.minPart(box);
    if (!/^-?\d+$/.test(value)) {
      return null;
    }
    return this.tempHex(parseInt(value, 10)) + 'CC';
  }

  /** Eingabe im Tiefstwert-Feld: bereinigen und unter '<boxId>.min' merken. */
  onMinInput(box: ImageBox, input: HTMLInputElement): void {
    const clean = this.sanitize(input.value);
    if (input.value !== clean) {
      input.value = clean;
    }
    box.minLabel = clean;
    this.rememberValue(box.id + ImageComponent.MIN_KEY_SUFFIX, clean);
  }

  /** Nur der numerische Teil des Icon-Anteils (der Symbol-Index). */
  iconPart(box: ImageBox): string {
    return this.sanitize(box.iconLabel ?? '');
  }

  /** Breite des Icon-Eingabefeldes passend zur Zeichenanzahl. */
  iconInputWidth(box: ImageBox): string {
    return Math.max(this.iconPart(box).length, 1) + 'ch';
  }

  /** Hintergrund der linken Zelle: Temperaturfarbe zum aktuellen Wert. */
  tempCellBackground(box: ImageBox): string | null {
    const value = this.numericPart(box);
    if (!/^-?\d+$/.test(value)) {
      return null;
    }
    return this.tempHex(parseInt(value, 10)) + 'CC';
  }

  /** Hintergrundbild der rechten Zelle: Symbol zum aktuellen Icon-Index. */
  iconCellImage(box: ImageBox): string {
    const folder = box.iconFolder ?? this.image?.iconFolder;
    if (!folder) {
      return 'none';
    }
    const value = this.iconPart(box);
    if (!/^\d+$/.test(value)) {
      return 'none';
    }
    const prefix = box.iconPrefix ?? this.image?.iconPrefix ?? '';
    const ext = box.iconExtension ?? this.image?.iconExtension ?? '';
    return `url("${folder}${prefix}${value}${ext}")`;
  }

  /** Eingabe im Icon-Feld: bereinigen und unter dem eigenen Schlüssel merken. */
  onIconInput(box: ImageBox, input: HTMLInputElement): void {
    const clean = this.sanitize(input.value);
    if (input.value !== clean) {
      input.value = clean;
    }
    box.iconLabel = clean;
    this.rememberValue(box.id + ImageComponent.ICON_KEY_SUFFIX, clean);
    this.merkeIconWert(clean);
  }

  /** Umschalter einer Box (leer, wenn keine konfiguriert sind). */
  togglesFor(box: ImageBox): ImageBoxToggle[] {
    return box.toggles ?? [];
  }

  /** Ist der Umschalter aktiviert (Symbol sichtbar)? */
  isToggleActive(box: ImageBox, toggle: ImageBoxToggle): boolean {
    return this.activeToggles.has(ImageComponent.toggleKey(box, toggle));
  }

  /**
   * Klick auf einen Umschalter: Symbol ein-/ausblenden. Das Event wird gestoppt,
   * damit der Klick nicht die Box-Bearbeitung auslöst.
   */
  onToggleClick(event: Event, box: ImageBox, toggle: ImageBoxToggle): void {
    event.stopPropagation();
    event.preventDefault();
    const key = ImageComponent.toggleKey(box, toggle);
    if (this.activeToggles.has(key)) {
      this.activeToggles.delete(key);
    } else {
      this.activeToggles.add(key);
    }
    this.spiegleUmschalter();
    this.merkeReihenfolge(box, toggle, this.activeToggles.has(key));
    if (toggle.id === KiGrafikService.KI_UMSCHALTER && !this.activeToggles.has(key)) {
      this.entfernePfeileVon(box.id);   // ein abgehaktes Kästchen hat keinen Griff mehr
    }
  }

  /**
   * Der gelbe Umschalter bekommt beim Anhaken seine Nummer in der kartenübergreifenden
   * Reihenfolge und verliert sie beim Abhaken ({@link KiReihenfolgeService}). Nach
   * {@link spiegleUmschalter}, weil der Service in `image.values` schreibt und das Spiegeln
   * die fremden `ki:`-Schlüssel nur durchreicht.
   */
  private merkeReihenfolge(box: ImageBox, toggle: ImageBoxToggle, an: boolean): void {
    if (toggle.id !== KiGrafikService.KI_UMSCHALTER || !this.image) {
      return;
    }
    if (an) {
      this.reihenfolge.anhaken(this.image, box.id);
    } else {
      this.reihenfolge.abhaken(this.image, box.id);
    }
  }

  /**
   * Die Nummer im blauen Kreis unten links: die Reihenfolge, in der das gelbe Kästchen
   * angehakt wurde – nur solange es angehakt ist. Liest ein Signal, deshalb aktualisiert
   * sich die Anzeige auch, wenn eine andere Karte die Zählung verschiebt.
   */
  reihenNummer(box: ImageBox): number | null {
    const toggle = (box.toggles ?? []).find(t => t.id === KiGrafikService.KI_UMSCHALTER);
    if (!toggle || !this.isToggleActive(box, toggle)) {
      return null;
    }
    return this.reihenfolge.nummer(this.image?.containerId, box.id);
  }

  /**
   * Größe des blauen Kreises: anderthalbmal die Umschalter der Box (`toggleScale`) – auf
   * Wunsch der Redaktion rund 50 % größer als die gelben Kästchen, damit die Nummer bei
   * kleinem Zoom lesbar bleibt.
   */
  reiheStil(box: ImageBox): { [key: string]: string } {
    return {'font-size': (box.toggleScale ?? 1) * ImageComponent.REIHE_GROESSE + 'em'};
  }

  /** Hover-Text des blauen Kreises: was die Nummer bedeutet. */
  reiheTitel(nummer: number): string {
    return `Nummerierung für den KI-Text: Nr. ${nummer} – die KI nennt die angehakten Regionen `
      + 'in dieser Reihenfolge';
  }

  /** Faktor des blauen Kreises gegenüber den Umschaltern der Box. */
  private static readonly REIHE_GROESSE = 1.5;

  /**
   * Legt die gesetzten Umschalter im Basis-Bucket ab, damit sie mitgespeichert werden.
   *
   * Sie liegen bewusst im Basis-Bucket und nicht je Abschnitt: `activeToggles` gilt für
   * das ganze Bild, nicht für einen Abschnitt. Ihre Schlüssel tragen `::` und können
   * deshalb nie mit einem Box-Wert kollidieren.
   */
  private spiegleUmschalter(): void {
    let bucket = this.sectionValues.get(ImageComponent.DEFAULT_BUCKET);
    if (!bucket) {
      bucket = new Map<string, string>();
      this.sectionValues.set(ImageComponent.DEFAULT_BUCKET, bucket);
    }
    Array.from(bucket.keys())
      .filter(key => key.includes(ImageComponent.TOGGLE_SEPARATOR))
      .forEach(key => bucket!.delete(key));
    this.activeToggles.forEach(key => bucket!.set(key, '1'));
    this.spiegleInsDokument();
  }

  /** Holt die gesetzten Umschalter aus den gespeicherten Werten zurück. */
  private ladeUmschalter(): void {
    this.activeToggles.clear();
    this.sectionValues.forEach(bucket => bucket.forEach((_wert, key) => {
      if (key.includes(ImageComponent.TOGGLE_SEPARATOR)) {
        this.activeToggles.add(key);
      }
    }));
  }

  private static toggleKey(box: ImageBox, toggle: ImageBoxToggle): string {
    return box.id + ImageComponent.TOGGLE_SEPARATOR + toggle.id;
  }

  /**
   * Schein unter der gesamten Box, sobald ein aktivierter Umschalter `glow` gesetzt hat
   * (z.B. der gelbe untere). Die Farbe kommt aus `glowColor`, ersatzweise vom Umschalter
   * selbst; bei 6-stelligem Hex wird – wie bei den Temperaturfarben – ein Alpha-Anteil
   * angehängt. Gibt null zurück, wenn kein solcher Umschalter aktiv ist.
   * Maße in em der Box-Schrift, damit der Schein mit dem Karten-Zoom mitwächst; auf Wunsch
   * der Redaktion rund 50 % größer und kräftiger als zuerst (0.65em Weichzeichnung, 0.18em
   * Ausdehnung, 75 % Deckkraft) – er soll auch bei kleinem Zoom sofort ins Auge fallen.
   */
  glowShadow(box: ImageBox): string | null {
    const toggle = (box.toggles ?? []).find(t => t.glow && this.isToggleActive(box, t));
    if (!toggle) {
      return null;
    }
    const color = ImageComponent.glowColor(toggle.glowColor ?? toggle.color);
    return `0 0.15em 1em 0.27em ${color}`;
  }

  /** Farbe des Scheins: 6-stelliges Hex bekommt eine hohe, aber nicht volle Deckkraft. */
  private static glowColor(color?: string): string {
    if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
      return color + 'F2';   // 0xF2 = 242 ≈ 95% Deckkraft
    }
    return color ?? 'rgba(168, 85, 247, 0.95)';
  }

  /**
   * Darstellung eines Umschalters. Über die Schriftgröße skaliert alles mit:
   * Kantenlänge (1em), Symbol (0.64em) und der seitliche Abstand.
   *
   * Bei vergrößerten Umschaltern reicht der Standardversatz nicht mehr – zwei
   * Umschalter wären zusammen höher als die Box und würden sich überlappen.
   * Der nötige Versatz wird deshalb aus Boxhöhe und Umschaltergröße berechnet.
   */
  toggleStyle(box: ImageBox, toggle: ImageBoxToggle): { [key: string]: string } {
    const faktor = box.toggleScale ?? 1;
    const groesse = (box.fontSize ?? 55) * faktor;          // im Referenzraum
    const noetig = (2 * groesse - box.height) / (2 * groesse);
    const versatz = Math.max(0.12, noetig);
    const stil: { [key: string]: string } = {'font-size': faktor + 'em'};
    if (toggle.position === 'bottom-right') {
      stil['bottom'] = -versatz + 'em';
    } else {
      stil['top'] = -versatz + 'em';
    }
    return stil;
  }

  /** trackBy für die Umschalter einer Box. */
  trackToggle = (_: number, toggle: ImageBoxToggle) => toggle.id;

  /** trackBy für die Box-Liste – stabile Identität, damit der Fokus beim Tippen nicht verloren geht. */
  trackBox = (_: number, box: ImageBox) => box.id;

  // ------------------------------------------------------------------
  // Bedienelemente der einzelnen Karte
  // ------------------------------------------------------------------

  /**
   * Ist das ein Lokalzeit-Bild, also eines mit Abschnitten und Quellenwahl?
   *
   * Merkmal sind die Abschnitte: die 12 Lokalzeit-Bilder haben welche, die 196 reinen
   * Grafik-Karten nicht.
   *
   * ⚠️ **Nicht** die `containerId` dafür nehmen. Die war früher das Merkmal (nur die 12
   * trugen eine), seit dem Speichern-Knopf hat sie aber **jedes** Bild – sie ist der
   * Schlüssel des Datensatzes. Wer wieder darauf abfragt, blendet die Karten-Knöpfe
   * überall aus und die FILE/DOC-Leiste überall ein.
   */
  private hatQuellenwahl(): boolean {
    return !!this.image?.sections?.length;
  }

  /** Zeigt diese Karte die FILE/DOC-Leiste? */
  zeigtQuellenwahl(): boolean {
    return this.hatQuellenwahl() && this.sourceButtons.length > 0;
  }

  /**
   * Zeigt diese Karte die eigenen Bedienelemente (Sync, Laden, Leeren)?
   *
   * Nur die reinen Grafik-Karten – und damit genau das Gegenstück zu
   * {@link zeigtQuellenwahl}: die Lokalzeit-Bilder tragen Abschnitts- und Quellen-Sidebar
   * an genau derselben Stelle oben links, dort würden sich die Elemente überdecken.
   */
  zeigtKartenBedienung(): boolean {
    return !this.hatQuellenwahl();
  }

  /** Hat die Karte überhaupt Wetter-Symbole? Ohne die ist der Sync-Schalter sinnlos. */
  hatWetterIcons(): boolean {
    return this.wetterIconBoxen().length > 0;
  }

  /** Ist der Sync bedienbar? Erst wenn ein Symbol als Vorlage vorliegt. */
  syncMoeglich(): boolean {
    return this.syncAktiv || !!this.letzterIconWert;
  }

  /** Erklärt am Schalter, warum er ggf. nicht bedienbar ist. */
  syncTitel(): string {
    if (!this.letzterIconWert) {
      return 'Sync: zuerst ein Wetter-Symbol in dieser Karte eintragen';
    }
    const partner = ImageComponent.syncPartnerVon(this.image?.containerId)
      ? ' – und ebenso in der Partnerkarte (feine/sehr feine Unterteilung)' : '';
    return this.syncAktiv
      ? `Sync aus: Symbole auf den Stand vor dem Einschalten zurücksetzen${partner}`
      : `Sync an: Symbol ${this.letzterIconWert} auf alle Symbole dieser Karte übertragen${partner}`;
  }

  /**
   * Die beiden NRW-Karten, deren Sync-Schalter gekoppelt sind: "feine Unterteilung"
   * (karte5) und "sehr feine Unterteilung" (karte6) derselben Zeile. Ein Druck auf den
   * einen Schalter wirkt wie ein Druck auf beide – aber nur innerhalb der Zeile, nie über
   * Zeitreihen hinweg (Oktober 2026).
   */
  private static readonly SYNC_PARTNER: {[karte: string]: string} = {karte5: 'karte6', karte6: 'karte5'};

  /** Die `containerId` der Partnerkarte, oder null, wenn die Karte keinen Sync-Partner hat. */
  static syncPartnerVon(containerId: string | undefined | null): string | null {
    const treffer = /^(wdr\.grafik\.[^.]+\.)(karte\d+)$/.exec(containerId ?? '');
    if (!treffer) {
      return null;
    }
    const partner = ImageComponent.SYNC_PARTNER[treffer[2]];
    return partner ? treffer[1] + partner : null;
  }

  /** Schalter umlegen: übertragen bzw. den vorherigen Stand wiederherstellen. */
  /**
   * Zeigt diese Karte den Schalter "Temp"? Nur "feine" und "sehr feine Unterteilung" der
   * NRW-Grafiken – die einzigen NRW-Karten mit Temperaturen
   * ({@link KiGrafikService.hatTemperaturSchalter}).
   */
  /**
   * Der Sync-Schalter lässt sich im Template abschalten (`showSyncSwitch: false`): auf der
   * Prognose-Karte stehen drei verschiedene Tage, ein Symbol auf alle zu übertragen wäre
   * dort falsch. Ohne Angabe bleibt er wie bisher sichtbar.
   */
  zeigtSyncSchalter(): boolean {
    return this.image?.showSyncSwitch !== false;
  }

  zeigtTempSchalter(): boolean {
    return this.zeigtKartenBedienung() && KiGrafikService.hatTemperaturSchalter(this.image);
  }

  /** Steht der Schalter "Temp" dieser Karte auf an? */
  get tempAktiv(): boolean {
    return KiGrafikService.istTemperaturAn(this.image);
  }

  tempTitel(): string {
    return this.tempAktiv
      ? 'Temp an: die Temperaturen dieser Karte gehen in die KI-Anfrage'
      : 'Temp aus: die Temperaturen dieser Karte bleiben aus der KI-Anfrage heraus';
  }

  /**
   * Schaltet "Temp" um. Der Zustand liegt direkt in `image.values` unter
   * `KiGrafikService.TEMP_SCHALTER` – einem fremden Schlüssel (`ki:`), den
   * {@link spiegleInsDokument} durchreicht. Damit speichert er mit dem Datensatz, ohne als
   * Box-Wert zu zählen, und "leeren" setzt ihn nicht zurück. Gespeichert wird immer
   * ausdrücklich `'1'` oder `'0'`: ohne Eintrag gälte wieder der Standard der Zeile
   * ({@link KiGrafikService.temperaturStandard}).
   */
  onTempToggle(): void {
    if (!this.image) {
      return;
    }
    this.image.values = {
      ...(this.image.values ?? {}),
      [KiGrafikService.TEMP_SCHALTER]: this.tempAktiv ? '0' : '1'
    };
    this.markiereGeaendert();
  }

  /**
   * Zeigt diese Karte den Schalter "Wind"? Nur Karten mit Wind-Boxen, die den gelben
   * Umschalter tragen – die NRW-Karte "Wind in Bft." ({@link KiGrafikService.hatWindSchalter}).
   */
  zeigtWindSchalter(): boolean {
    return this.zeigtKartenBedienung() && KiGrafikService.hatWindSchalter(this.image);
  }

  /**
   * Die Region der landesweiten Wind-Box ("NRW ganz", rechts unten auf der Wind-Karte). Sie
   * ist eine Aussage über das ganze Land, keine Teilregion – der Sammelschalter "Wind" lässt
   * sie bewusst unberührt. Angehakt wird sie nur einzeln; in der Zeile "HEUTE: ganz" ist sie
   * als einzige Wind-Box vorbelegt (das Template bringt nur ihren Schlüssel mit).
   */
  static readonly LANDESWEITE_WIND_REGION = 'NRW ganz';

  /**
   * Die Wind-Boxen dieser Karte, die der Sammelschalter "Wind" bedient, samt gelbem
   * Umschalter – alle Teilregionen, nicht die landesweite Box
   * ({@link LANDESWEITE_WIND_REGION}).
   */
  private windUmschalter(): {box: ImageBox; toggle: ImageBoxToggle}[] {
    const paare: {box: ImageBox; toggle: ImageBoxToggle}[] = [];
    for (const box of this.allBoxes()) {
      const toggle = this.isWind(box) && box.region !== ImageComponent.LANDESWEITE_WIND_REGION
        ? (box.toggles ?? []).find(t => t.id === KiGrafikService.KI_UMSCHALTER) : undefined;
      if (toggle) {
        paare.push({box, toggle});
      }
    }
    return paare;
  }

  /**
   * Steht der Schalter "Wind" auf an? Er hat keinen eigenen Zustand: an heißt, dass alle
   * gelben Kästchen der regionalen Wind-Boxen angehakt sind ("NRW ganz" zählt nicht mit).
   * Wer eines einzeln abwählt, sieht ihn auf aus springen – der nächste Klick hakt dann
   * wieder alle an.
   */
  get windAktiv(): boolean {
    const paare = this.windUmschalter();
    return paare.length > 0 && paare.every(p => this.isToggleActive(p.box, p.toggle));
  }

  windTitel(): string {
    return this.windAktiv
      ? 'Wind an: alle gelben Kästchen der regionalen Wind-Boxen sind angehakt – Klick wählt alle ab ("NRW ganz" bleibt, wie es ist)'
      : 'Wind aus: Klick hakt alle gelben Kästchen der regionalen Wind-Boxen an ("NRW ganz" bleibt, wie es ist)';
  }

  /**
   * Sammelschalter der gelben Kästchen: alle regionalen an oder alle aus, "NRW ganz" bleibt
   * unberührt. Der Zustand liegt nirgends extra, sondern in den Umschaltern selbst
   * (`<boxId>::ai` in `image.values`, wie beim Klick auf ein einzelnes Kästchen über
   * {@link spiegleUmschalter}) – gespeichert wird also genau das, was die Kästchen zeigen.
   * Standardmäßig ist keine regionale Wind-Box angehakt; nur in der Zeile "HEUTE: ganz" ist
   * die landesweite Box "NRW ganz" vorbelegt, die der Schalter ohnehin nicht anfasst.
   */
  onWindToggle(): void {
    const paare = this.windUmschalter();
    if (paare.length === 0) {
      return;
    }
    const alleAb = this.windAktiv;
    const geaendert: {box: ImageBox; toggle: ImageBoxToggle}[] = [];
    for (const {box, toggle} of paare) {
      const key = ImageComponent.toggleKey(box, toggle);
      if (alleAb && this.activeToggles.has(key)) {
        this.activeToggles.delete(key);
        geaendert.push({box, toggle});
      } else if (!alleAb && !this.activeToggles.has(key)) {
        this.activeToggles.add(key);
        geaendert.push({box, toggle});
      }
    }
    this.spiegleUmschalter();
    // Die Reihenfolge-Nummern folgen der Kartenreihenfolge der Boxen – so, als wären sie
    // nacheinander einzeln angehakt worden.
    geaendert.forEach(({box, toggle}) => this.merkeReihenfolge(box, toggle, !alleAb));
    if (alleAb) {
      geaendert.forEach(({box}) => this.entfernePfeileVon(box.id));
    }
    this.markiereGeaendert();
  }

  onSyncToggle(): void {
    if (this.syncAktiv) {
      this.syncAus();
    } else {
      this.syncAn();
    }
    // Feine und sehr feine Unterteilung sind gekoppelt: die Partnerkarte derselben Zeile legt
    // ihren Schalter mit um. Sie bekommt die eigene Vorlage mit, falls sie selbst keine hat.
    const partnerId = ImageComponent.syncPartnerVon(this.image?.containerId);
    if (partnerId) {
      this.eventBusService.emit(EventBusTypes.SYNC_PARTNER_EVENT.valueOf(),
        new SyncPartner(this.image!.containerId, partnerId, this.syncAktiv, this.letzterIconWert));
    }
  }

  /**
   * Die Partnerkarte hat ihren Sync umgelegt: hier dasselbe tun, als wäre der eigene Schalter
   * gedrückt worden. Eine eigene Vorlage (zuletzt hier eingetipptes Symbol) hat Vorrang; ohne
   * eigene übernimmt die Karte das Symbol der Partnerkarte als Vorlage – sonst bliebe der
   * Schalter wirkungslos, weil es nichts zu übertragen gäbe. Ist der Zustand schon erreicht
   * (beide Schalter wurden einzeln gedrückt), passiert nichts. Kommt aus einem Bus-Callback,
   * also `markiereGeaendert()`.
   */
  private folgeSyncDesPartners(partner: SyncPartner): void {
    if (partner.an === this.syncAktiv) {
      return;
    }
    if (partner.an) {
      if (!this.letzterIconWert && partner.symbol) {
        this.letzterIconWert = partner.symbol;
      }
      this.syncAn();
    } else {
      this.syncAus();
    }
    this.markiereGeaendert();
  }

  /** "MSWR-MOS laden" nur für diese Karte. */
  onKarteLaden(): void {
    this.ladeWerte();
  }

  /** "leeren" nur für diese Karte. */
  onKarteLeeren(): void {
    this.clearAllBoxes();
    this.syncAktiv = false;
    this.syncVorher.clear();
    this.letzterIconWert = null;
  }

  /**
   * Merkt den zuletzt eingetippten Symbol-Wert und überträgt ihn sofort weiter,
   * solange der Sync läuft – der Schalter heißt Sync und verhält sich auch so.
   * Der Stand für die Wiederherstellung bleibt der beim Einschalten aufgenommene.
   */
  private merkeIconWert(wert: string): void {
    this.letzterIconWert = wert;
    if (this.syncAktiv && wert !== '') {
      this.uebertrageAufAlle(wert);
    }
  }

  private syncAn(): void {
    const wert = this.letzterIconWert;
    if (!wert) {
      return;   // ohne Vorlage gibt es nichts zu übertragen
    }
    this.syncVorher.clear();
    for (const box of this.wetterIconBoxen()) {
      this.syncVorher.set(box.id, this.iconWertVon(box));
    }
    this.uebertrageAufAlle(wert);
    this.syncAktiv = true;
  }

  private syncAus(): void {
    for (const box of this.wetterIconBoxen()) {
      const vorher = this.syncVorher.get(box.id);
      if (vorher !== undefined) {
        this.setzeIconWert(box, vorher);
      }
    }
    this.syncVorher.clear();
    this.syncAktiv = false;
  }

  private uebertrageAufAlle(wert: string): void {
    for (const box of this.wetterIconBoxen()) {
      this.setzeIconWert(box, wert);
    }
  }

  /**
   * Boxen mit Wetter-Symbol: reine Symbol-Boxen und der Symbol-Anteil der
   * Kombi-Boxen. Warnschilder ({@code danger}) bleiben außen vor – die zeigen ein
   * Unwettersymbol aus einem anderen Ordner, kein Wetter-Symbol.
   */
  private wetterIconBoxen(): ImageBox[] {
    return this.allBoxes().filter(box => box.type === 'icon' || this.istKombi(box));
  }

  private iconWertVon(box: ImageBox): string {
    return this.istKombi(box) ? this.iconPart(box) : this.numericPart(box);
  }

  /** Schreibt einen Symbol-Wert in Anzeige und Wert-Cache – wie eine Eingabe. */
  private setzeIconWert(box: ImageBox, wert: string): void {
    if (this.istKombi(box)) {
      this.rememberValue(box.id + ImageComponent.ICON_KEY_SUFFIX, wert);
      box.iconLabel = wert;
    } else {
      this.rememberValue(box.id, wert);
      box.label = wert + this.unit(box);
    }
  }

  /** Nur der numerische Teil des Labels (ohne Einheit) – das ist der editierbare Wert. */
  numericPart(box: ImageBox): string {
    return this.sanitize(box.label ?? '');
  }

  /** Breite des Eingabefeldes passend zur Zeichenanzahl, damit die Einheit direkt anschließt. */
  inputWidth(box: ImageBox): string {
    return Math.max(this.numericPart(box).length, 1) + 'ch';
  }

  /** Klick: Box in den Bearbeitungsmodus versetzen und das Eingabefeld fokussieren. */
  startEdit(box: ImageBox, input: HTMLInputElement): void {
    // Der Auslöser ist dreifach: Klick auf den Box-Rahmen, Klick auf das Feld selbst
    // und – für die Tastatur – der Fokus. Ein Feld ist bis dahin readonly; ohne den
    // Fokus-Auslöser konnte man per Tab hineinspringen, aber nichts eintippen.
    //
    // Der Riegel schützt den Cursor: ist das Feld schon offen UND fokussiert, kommt
    // der Aufruf von einem Klick hinein (oder von einem Klick, der aus dem Feld auf
    // den Rahmen hochblubbert). Dann darf der Wert nicht erneut komplett markiert
    // werden, sonst reißt es dem Redakteur die Einfügestelle weg.
    if (this.editingId === box.id && document.activeElement === input) {
      return;
    }
    this.editingId = box.id;
    // Fokus/Auswahl erst, nachdem das readonly-Attribut per Bindung entfernt wurde.
    // Beim Hineinspringen per Tab ist das Feld bereits fokussiert; focus() ist dann
    // ein No-Op und select() markiert den Wert, sodass Tippen ihn ersetzt.
    setTimeout(() => {
      // Beim schnellen Durchtabben kann der Fokus längst weitergewandert sein. Dann
      // darf dieser verzögerte Aufruf ihn nicht zurückreißen.
      if (this.editingId !== box.id) {
        return;
      }
      input.focus();
      input.select();
    });
  }

  stopEdit(): void {
    this.editingId = null;
  }

  /** Verhindert nicht-numerische Eingaben (erlaubt Ziffern, führendes Minus und Steuertasten). */
  onKeydown(event: KeyboardEvent): void {
    const control = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'Tab', 'Enter', 'Escape'];
    if (control.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    if (/^[0-9]$/.test(event.key) || event.key === '-') {
      return;
    }
    event.preventDefault();
  }

  /** Bereinigt den Wert (z.B. nach Einfügen) und schreibt ihn inkl. Einheit zurück ins Modell. */
  onInput(box: ImageBox, input: HTMLInputElement): void {
    const clean = this.sanitize(input.value);
    if (input.value !== clean) {
      input.value = clean;
    }
    box.label = clean + this.unit(box);
    // Änderung je Abschnitt merken, damit sie beim Umschalten erhalten bleibt
    this.rememberValue(box.id, clean);
    if (box.type === 'icon') {
      this.merkeIconWert(clean);
    }
  }

  /** Behält nur Ziffern und ein einzelnes führendes Minus. */
  private sanitize(value: string): string {
    let v = value.replace(/[^0-9-]/g, '');   // nur Ziffern und Minus
    const negative = v.startsWith('-');
    v = v.replace(/-/g, '');                  // alle Minus entfernen ...
    return (negative ? '-' : '') + v;         // ... und ggf. genau eines voranstellen
  }
}
